import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import ReactDOMServer from "react-dom/server";
import { useDispatch, useSelector } from "react-redux";
import cx from "classnames";
import { useParams } from "react-router-dom";

import global from "../../../scss/dhp.scss";

import { Icon } from "../../ui/icon";
import {
  Card,
  CardBody,
  CardHeader,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Tooltip,
  UncontrolledTooltip,
} from "reactstrap";
import { widgetConfig } from "../editor_config";
import useDragAndDrop from "../../../hooks/useDragAndDropV2";
import { Input } from "../../ui/input";
import Modal from "../../ui/modal";
import { dateConverter, generateUniqueHash, hexToRGB, validateUrl } from "../../../_helpers/utils";
import { ITINERARY } from "../../../constants/editor";
import useAlignment from "../../../hooks/useAlignment";
import useAddWidget from "../../../hooks/useAddWidget";
import useElementInnerHtml from "../../../hooks/useElementInnerHtml";
import { EditorContext } from "../../../containers/editor/EditorLayout";

import AssetField from "../../ui/AssetField";
import { uploadItineraryAsset } from "../../../store/actions/documentActions";
import DocumentAssetField from "../../ui/DocumentAssetField";
import { getResourceEntries } from "../../../store/actions/resourcesActions";
import DescriptionAutoResizingTextarea from "../../Common/DescriptionAutoResizingTextarea";
import DeleteElemModal from "../Modals/DeleteElemModal";
import { fetchTravelSupplierByType, setUsedProposalModule } from "../../../store/actions/companyActions";
import useProposalInUseModule from "../../../hooks/useProposalInUseModule";
import { FlashAlert } from "../../ui/flashAlert";
import ModalFlashMessage from "../../ui/ModalFlashMessage";

let style = Object.assign({}, global);

const FlashMessage = ({ flashMessage }) => {
  const [flash, setflash] = useState({});
  const [visible, setVisible] = useState(false);
  const dispatch = useDispatch();
  const allStates = useSelector(state => state);

  const toggleFlash = () => setVisible(!visible);
  const exceptions = [];

  const handleChange = state => {
    let isflashMessage = Object.keys(state).some(reducer => state[reducer].flashMessage);
    let isStatusInvalid = Object.keys(state).some(
      reducer => state[reducer].status === 401 && !exceptions.includes(state[reducer].type)
    );
    if (isStatusInvalid) {
      setTimeout(() => {
        dispatch(logout());
        dispatch(clearCurrentUserSession());
      }, 300);
    }
    if (isflashMessage && !isStatusInvalid) {
      let reducer = Object.keys(state).find(reducer => state[reducer].flashMessage);
      setflash(state[reducer].flashMessage);
      setVisible(true);
    }
  };

  useEffect(() => {
    handleChange(allStates);
  }, [allStates]);

  useEffect(() => {
    if (flashMessage) {
      setflash(flashMessage);
      setVisible(true);
    }
  }, [flashMessage]);

  return (
    <FlashAlert
      isOpen={visible}
      toggle={toggleFlash}
      type={flash.type}
      message={flash.message}
      component={flash.component}
    />
  );
};

//For Description
const FloatingTooltipComponent = ({
  applyBoldFn,
  tooltipFor,
  handleBulletApply,
  bulletActive,
  defaltFontBold,
  isBoldeAvailable,
  horizontalScroll,
  bulletDisable,
}) => {
  const elementRef = useRef(null);

  let tableDiv = document.querySelector(".TMright-pane");

  useEffect(() => {
    setTimeout(() => {
      if (elementRef.current && tableDiv) {
        const elementRect = elementRef.current.getBoundingClientRect();
        const paneRect = tableDiv.getBoundingClientRect();

        // Check if the element has crossed the right pane and left pane
        if (elementRect.left <= paneRect.left || elementRect.right >= paneRect.right) {
          elementRef.current.parentElement.classList.add("invisible");
        } else {
          elementRef.current.parentElement.classList.remove("invisible");
        }
      }
    }, 50);
  }, [horizontalScroll]);

  return (
    <div id="dhpTableCellEditableTooltip" className={cx(style[""])} ref={elementRef}>
      <ul className={style["d-flex"]}>
        {tooltipFor?.formattingOpt && (
          <>
            <li
              className={cx(style["toolset-group"], {
                [style["active"]]: defaltFontBold,
                [style["disable"]]: isBoldeAvailable,
              })}
              onMouseDown={e => {
                e.preventDefault();
                e.stopPropagation();
                applyBoldFn();
              }}>
              <div className={style["toolset-item"]}>
                <div className={cx(style["toolset-action"], style["custom-tooltip"])}>
                  <Icon icon="bold2" />
                  <div className={cx(style["custom-tooltip-content"], style["top"])}>Bold</div>
                </div>
              </div>
            </li>
            {tooltipFor?.enableBullet && (
              <li
                className={cx(
                  style["toolset-group"],
                  { [style["active"]]: bulletActive },
                  { [style["disable"]]: bulletDisable }
                )}
                onMouseDown={e => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleBulletApply();
                }}>
                <div className={style["toolset-item"]}>
                  <div className={cx(style["toolset-action"], style["custom-tooltip"])}>
                    <Icon icon="ui-list" />
                    <div className={cx(style["custom-tooltip-content"], style["top"])}>Bullets</div>
                  </div>
                </div>
              </li>
            )}
          </>
        )}
      </ul>
    </div>
  );
};

const CanvasHtmlData = ({ canvasConfigData }) => {
  const textCountFn = htmlDataStr => {
    const textOnly = htmlDataStr.replace(/<[^>]*>/g, "");
    return textOnly.length;
  };

  const sectionHeaderStyle = {};
  if (canvasConfigData?.sectionHeaderColor) {
    sectionHeaderStyle.backgroundColor = hexToRGB(canvasConfigData.sectionHeaderColor);
  }
  if (canvasConfigData?.sectionBorderColor) {
    sectionHeaderStyle.borderBottom = `1px solid ${hexToRGB(canvasConfigData.sectionBorderColor)}`;
  }

  const sectionSubHeaderStyle = {};
  if (canvasConfigData?.sectionSubheaderColor) {
    sectionSubHeaderStyle.backgroundColor = hexToRGB(canvasConfigData.sectionSubheaderColor);
  }
  if (canvasConfigData?.sectionBorderColor) {
    sectionSubHeaderStyle.borderBottom = `1px solid ${hexToRGB(canvasConfigData.sectionBorderColor)}`;
  }

  const sectionBodyStyle = {};
  if (canvasConfigData?.sectionBodyColor) {
    sectionBodyStyle.backgroundColor = hexToRGB(canvasConfigData.sectionBodyColor);
  }
  if (canvasConfigData?.sectionBorderColor) {
    sectionBodyStyle.borderBottom = `1px solid ${hexToRGB(canvasConfigData.sectionBorderColor)}`;
  }
  if (canvasConfigData?.sectionBodyContentColor) {
    sectionBodyStyle.color = hexToRGB(canvasConfigData.sectionBodyContentColor);
  }

  // Content Color section
  const sectionHeaderContentStyle = {};
  if (canvasConfigData?.sectionHeaderContentColor) {
    sectionHeaderContentStyle.color = hexToRGB(canvasConfigData.sectionHeaderContentColor);
  }

  const sectionSubheaderContentStyle = {};
  if (canvasConfigData?.sectionSubheaderContentColor) {
    sectionSubheaderContentStyle.color = hexToRGB(canvasConfigData.sectionSubheaderContentColor);
  }
  return (
    <div
      className={cx(style["w-100"], style["pricingTable_editor"], style["itinerary_editor"])}
      style={canvasConfigData?.["font-family"] ? { fontFamily: canvasConfigData["font-family"] } : {}}>
      <div
        className={cx(style["card"], style["pricing-table-section"])}
        style={
          canvasConfigData?.sectionBorderColor ? { border: `1px solid ${canvasConfigData.sectionBorderColor}` } : {}
        }>
        <div className={cx(style["card-header"])} style={sectionHeaderStyle}>
          <div className={cx(style["d-flex"], style["align-items-center"], style["ps-2"], style["ms-1"])}>
            <Icon
              icon="ui-calendar"
              additionalclass={!sectionHeaderContentStyle.color && "color-white"}
              style={sectionHeaderContentStyle}
            />
            <h6 className={cx(style["mb-0"], style["fw-7"], style["ms-2"])} style={sectionHeaderContentStyle}>
              {dateConverter(canvasConfigData?.itinerary_date, "ddd MMM DD, YYYY", "toString")} |{" "}
              {canvasConfigData?.itinerary_name}
            </h6>
          </div>
        </div>

        <div className={cx(style["p-0"], style["card-body"])}>
          {canvasConfigData?.items?.map((itemInfo, itemIdx) => (
            <React.Fragment key={itemIdx}>
              <div className={style["item-label"]} style={sectionSubHeaderStyle}>
                <ul className={cx(style["ps-2"])}>
                  <li className={cx(style["d-flex"], style["align-items-center"], style["ms-0"])}>
                    <Icon icon={`ui-${itemInfo?.icon}`} style={sectionSubheaderContentStyle} />
                    <span className={style["ms-2"]} style={sectionSubheaderContentStyle}>
                      {itemInfo?.title}
                    </span>
                  </li>
                </ul>
              </div>

              <div className={style["item-details"]} style={sectionBodyStyle}>
                <div>
                  {/* Flight */}
                  {itemInfo?.slug == "flight" && (
                    <>
                      <p>
                        <span className={style["fw-7"]}>Flight:</span> {itemInfo?.airline.name},{" "}
                        {itemInfo?.flight_number}
                      </p>

                      <div className={style["row"]}>
                        <div className={style["col-6"]}>
                          <span className={style["fw-7"]}>Departure</span>
                          <p>
                            {dateConverter(itemInfo?.departure_date_and_time, "ddd MMM DD, YYYY hh:mm A", "toString")}
                            <br></br>
                            {itemInfo?.departure_airport}
                          </p>
                        </div>
                        <div className={style["col-6"]}>
                          <span className={style["fw-7"]}>Arrival</span>
                          <p>
                            {dateConverter(itemInfo?.arrival_date_and_time, "ddd MMM DD, YYYY hh:mm A", "toString")}
                            <br></br>
                            {itemInfo?.arrival_airport}
                          </p>
                        </div>
                      </div>

                      {(itemInfo?.class || itemInfo?.confirmation_number || itemInfo?.seats) && (
                        <p>
                          {itemInfo?.class && (
                            <>
                              <span className={style["fw-7"]}>Class:</span> {itemInfo.class}
                            </>
                          )}
                          {itemInfo?.class && itemInfo?.confirmation_number && " | "}
                          {itemInfo?.confirmation_number && (
                            <>
                              <span className={style["fw-7"]}>Confirmation Number:</span> {itemInfo.confirmation_number}
                            </>
                          )}
                          {(itemInfo?.class || itemInfo?.confirmation_number) && itemInfo?.seats && <br></br>}
                          {itemInfo?.seats && (
                            <>
                              <span className={style["fw-7"]}>Seats:</span> {itemInfo.seats}
                            </>
                          )}
                        </p>
                      )}
                    </>
                  )}

                  {/* Accommodation */}
                  {itemInfo?.slug == "accommodation" && (
                    <>
                      <p>
                        <span className={style["fw-7"]}>{itemInfo?.property_name.name}</span>
                        <br></br>
                        {itemInfo?.address}
                        <br></br>
                        <span className={cx(style["d-flex"], style["align-items-center"])}>
                          <Icon icon="ui-location" additionalclass={"me-1 font-base"} />
                          <a
                            className={cx(style["text-decoration-underline"], {
                              [style["color-black"]]:
                                canvasConfigData.sectionBodyContentColor === "#000000" ||
                                !canvasConfigData.sectionBodyContentColor,
                              [style["color-white"]]: canvasConfigData.sectionBodyContentColor === "#ffffff",
                            })}
                            data-address={itemInfo?.address}>
                            View on Map
                          </a>
                        </span>
                      </p>
                      <div className={style["row"]}>
                        <div className={style["col-6"]}>
                          <span className={style["fw-7"]}>Check-in</span>
                          <p>
                            {dateConverter(itemInfo?.check_in_date_and_time, "ddd MMM DD, YYYY hh:mm A", "toString")}
                          </p>
                        </div>
                        <div className={style["col-6"]}>
                          <span className={style["fw-7"]}>Check-out</span>
                          <p>
                            {dateConverter(itemInfo?.check_out_date_and_time, "ddd MMM DD, YYYY hh:mm A", "toString")}
                          </p>
                        </div>
                      </div>
                      {(itemInfo?.room_type || itemInfo?.confirmation_number) && (
                        <p>
                          {itemInfo?.room_type && (
                            <>
                              <span className={style["fw-7"]}>Room Types:</span> {itemInfo.room_type}
                            </>
                          )}
                          {itemInfo?.room_type && itemInfo?.confirmation_number && <br></br>}
                          {itemInfo?.confirmation_number && (
                            <>
                              <span className={style["fw-7"]}>Confirmation Number:</span> {itemInfo.confirmation_number}
                            </>
                          )}
                        </p>
                      )}
                    </>
                  )}
                  {/* Meal */}
                  {itemInfo?.slug == "meal" && (
                    <>
                      <p>
                        <span className={style["fw-7"]}>{itemInfo?.restaurant_name.name}</span>
                        <br></br>
                        {itemInfo?.address && (
                          <>
                            {itemInfo.address}
                            <br></br>
                            <span className={cx(style["d-flex"], style["align-items-center"])}>
                              <Icon icon="ui-location" additionalclass={"me-1 font-base"} />
                              <a
                                className={cx(style["text-decoration-underline"], {
                                  [style["color-black"]]:
                                    canvasConfigData.sectionBodyContentColor === "#000000" ||
                                    !canvasConfigData.sectionBodyContentColor,
                                  [style["color-white"]]: canvasConfigData.sectionBodyContentColor === "#ffffff",
                                })}
                                data-address={itemInfo.address}>
                                View on Map
                              </a>
                            </span>
                          </>
                        )}
                      </p>
                      <div className={style["row"]}>
                        <div className={style["col-6"]}>
                          <span className={style["fw-7"]}>Dining Time</span>
                          <p>{dateConverter(itemInfo?.meal_date_and_time, "ddd MMM DD, YYYY hh:mm A", "toString")}</p>
                        </div>
                      </div>
                      {(itemInfo?.cuisine || itemInfo?.confirmation_number) && (
                        <p>
                          {itemInfo?.cuisine && (
                            <>
                              <span className={style["fw-7"]}>Cuisine:</span> {itemInfo.cuisine}
                            </>
                          )}
                          {itemInfo?.cuisine && itemInfo?.confirmation_number && <br></br>}
                          {itemInfo?.confirmation_number && (
                            <>
                              <span className={style["fw-7"]}>Confirmation Number:</span> {itemInfo.confirmation_number}
                            </>
                          )}
                        </p>
                      )}
                    </>
                  )}

                  {/* Car Rental */}
                  {itemInfo?.slug == "car_rental" && (
                    <>
                      <p>
                        <span className={style["fw-7"]}>Rental Agency:</span> {itemInfo?.rental_agency_name.name}
                      </p>
                      <div className={style["row"]}>
                        <div className={style["col-6"]}>
                          <span className={style["fw-7"]}>Pickup</span>
                          <p>
                            {dateConverter(itemInfo?.pickup_date_and_time, "ddd MMM DD, YYYY hh:mm A", "toString")}
                            <br></br>
                            {itemInfo?.pickup_location}
                          </p>
                        </div>
                        <div className={style["col-6"]}>
                          <span className={style["fw-7"]}>Drop-off</span>
                          <p>
                            {dateConverter(itemInfo?.drop_off_date_and_time, "ddd MMM DD, YYYY hh:mm A", "toString")}
                            <br></br>
                            {itemInfo?.drop_off_location}
                          </p>
                        </div>
                      </div>
                      {(itemInfo?.vehicle_type || itemInfo?.confirmation_number) && (
                        <p>
                          {itemInfo?.vehicle_type && (
                            <>
                              <span className={style["fw-7"]}>Vehicle Type:</span> {itemInfo.vehicle_type}
                            </>
                          )}
                          {itemInfo?.vehicle_type && itemInfo?.confirmation_number && <br></br>}
                          {itemInfo?.confirmation_number && (
                            <>
                              <span className={style["fw-7"]}>Confirmation Number:</span> {itemInfo.confirmation_number}
                            </>
                          )}
                        </p>
                      )}
                    </>
                  )}

                  {/* Cruise */}
                  {itemInfo?.slug == "cruise" && (
                    <>
                      <p>
                        <span className={style["fw-7"]}>Cruise Ship:</span> {itemInfo?.cruise_line.name}
                      </p>
                      <div className={style["row"]}>
                        <div className={style["col-6"]}>
                          <span className={style["fw-7"]}>Departure</span>
                          <p>
                            {dateConverter(itemInfo?.departure_date_and_time, "ddd MMM DD, YYYY hh:mm A", "toString")}
                            <br></br>
                            {itemInfo?.departure_port}
                          </p>
                        </div>
                        <div className={style["col-6"]}>
                          <span className={style["fw-7"]}>Arrival</span>
                          <p>
                            {dateConverter(itemInfo?.arrival_date_and_time, "ddd MMM DD, YYYY hh:mm A", "toString")}
                            <br></br>
                            {itemInfo?.arrival_port}
                          </p>
                        </div>
                      </div>
                      {(itemInfo?.number_of_port ||
                        itemInfo?.confirmation_number ||
                        itemInfo?.cabin_number ||
                        itemInfo?.cabin_type) && (
                        <p>
                          {itemInfo?.number_of_port && (
                            <>
                              <span className={style["fw-7"]}>Number of Ports:</span> {itemInfo.number_of_port}
                            </>
                          )}
                          {itemInfo?.number_of_port && itemInfo?.confirmation_number && " | "}
                          {itemInfo?.confirmation_number && (
                            <>
                              <span className={style["fw-7"]}>Confirmation Number:</span> {itemInfo.confirmation_number}
                            </>
                          )}

                          {(itemInfo?.number_of_port || itemInfo?.confirmation_number) &&
                            (itemInfo?.cabin_number || itemInfo?.cabin_type) && <br></br>}

                          {itemInfo?.cabin_number && (
                            <>
                              <span className={style["fw-7"]}>Cabin Number:</span> {itemInfo.cabin_number}
                            </>
                          )}
                          {itemInfo?.cabin_number && itemInfo?.cabin_type && " | "}
                          {itemInfo?.cabin_type && (
                            <>
                              <span className={style["fw-7"]}>Cabin Type:</span> {itemInfo.cabin_type}
                            </>
                          )}
                        </p>
                      )}
                    </>
                  )}

                  {/* Train */}
                  {itemInfo?.slug == "train" && (
                    <>
                      <p>
                        <span className={style["fw-7"]}>Train:</span> {itemInfo?.train_number}
                      </p>
                      <div className={style["row"]}>
                        <div className={style["col-6"]}>
                          <span className={style["fw-7"]}>Departure</span>
                          <p>
                            {dateConverter(itemInfo?.departure_date_and_time, "ddd MMM DD, YYYY hh:mm A", "toString")}
                            <br></br>
                            {itemInfo?.departure_station}
                          </p>
                        </div>
                        <div className={style["col-6"]}>
                          <span className={style["fw-7"]}>Arrival</span>
                          <p>
                            {dateConverter(itemInfo?.arrival_date_and_time, "ddd MMM DD, YYYY hh:mm A", "toString")}
                            <br></br>
                            {itemInfo?.arrival_station}
                          </p>
                        </div>
                      </div>
                      {(itemInfo?.class || itemInfo?.confirmation_number) && (
                        <p>
                          {itemInfo?.class && (
                            <>
                              <span className={style["fw-7"]}>Class:</span> {itemInfo.class}
                            </>
                          )}
                          {itemInfo?.class && itemInfo?.confirmation_number && <br></br>}
                          {itemInfo?.confirmation_number && (
                            <>
                              <span className={style["fw-7"]}>Confirmation Number:</span>{" "}
                              {itemInfo?.confirmation_number}
                            </>
                          )}
                        </p>
                      )}
                    </>
                  )}

                  {/* Activity */}
                  {itemInfo?.slug == "activity" && (
                    <>
                      <p>
                        <span className={style["fw-7"]}>Provider:</span> {itemInfo?.provider_name.name}
                      </p>
                      <div className={style["row"]}>
                        <div className={style["col-6"]}>
                          <span className={style["fw-7"]}>Location and Timing</span>
                          <p>
                            {dateConverter(itemInfo?.activity_date_and_Time, "ddd MMM DD, YYYY hh:mm A", "toString")}
                            <br></br>
                            {itemInfo?.activity_location}
                          </p>
                        </div>
                        <div className={style["col-6"]}>
                          <span className={style["fw-7"]}>Pickup</span>
                          <p>
                            {dateConverter(itemInfo?.pickup_date_and_Time, "ddd MMM DD, YYYY hh:mm A", "toString")}
                            <br></br>
                            {itemInfo?.pickup_location}
                          </p>
                        </div>
                      </div>
                      {(itemInfo?.duration || itemInfo?.confirmation_number) && (
                        <p>
                          {itemInfo.duration && (
                            <>
                              <span className={style["fw-7"]}>Duration:</span> {itemInfo.duration}
                            </>
                          )}
                          {itemInfo?.duration && itemInfo?.confirmation_number && <br></br>}
                          {itemInfo?.confirmation_number && (
                            <>
                              <span className={style["fw-7"]}>Confirmation Number:</span> {itemInfo.confirmation_number}
                            </>
                          )}
                        </p>
                      )}
                    </>
                  )}

                  {/* Transfer */}
                  {itemInfo?.slug == "transfer" && (
                    <>
                      <p>
                        <span className={style["fw-7"]}>Provider:</span> {itemInfo?.provider_name.name}
                      </p>
                      <div className={style["row"]}>
                        <div className={style["col-6"]}>
                          <span className={style["fw-7"]}>Pickup</span>
                          <p>
                            {dateConverter(itemInfo?.pickup_date_and_Time, "ddd MMM DD, YYYY hh:mm A", "toString")}
                            <br></br>
                            {itemInfo?.pickup_location}
                          </p>
                        </div>
                        <div className={style["col-6"]}>
                          <span className={style["fw-7"]}>Drop-off</span>
                          <p>
                            {dateConverter(itemInfo?.drop_off_date_and_Time, "ddd MMM DD, YYYY hh:mm A", "toString")}
                            <br></br>
                            {itemInfo?.drop_off_location}
                          </p>
                        </div>
                      </div>
                      {(itemInfo?.vehicle_type || itemInfo?.confirmation_number) && (
                        <p>
                          {itemInfo?.vehicle_type && (
                            <>
                              <span className={style["fw-7"]}>Vehicle Type:</span> {itemInfo.vehicle_type}
                            </>
                          )}

                          {itemInfo?.vehicle_type && itemInfo?.confirmation_number && <br></br>}

                          {itemInfo.confirmation_number && (
                            <>
                              <span className={style["fw-7"]}>Confirmation Number:</span> {itemInfo.confirmation_number}
                            </>
                          )}
                        </p>
                      )}
                    </>
                  )}

                  {["accommodation", "meal", "car_rental", "cruise", "activity", "transfer"].includes(itemInfo.slug) &&
                    (itemInfo?.phone_pnumber || itemInfo?.website) && (
                      <p className={style["d-flex"]}>
                        {itemInfo?.phone_pnumber && (
                          <span className={cx(style["d-flex"], style["align-items-center"], style["me-4"])}>
                            <Icon icon="ui-phone" additionalclass={"me-1 font-base"} />
                            <a
                              className={cx(style["text-decoration-underline"], {
                                [style["color-black"]]:
                                  canvasConfigData.sectionBodyContentColor === "#000000" ||
                                  !canvasConfigData.sectionBodyContentColor,
                                [style["color-white"]]: canvasConfigData.sectionBodyContentColor === "#ffffff",
                              })}
                              data-phone-href={`tel:${itemInfo.phone_isd_code}${itemInfo.phone_pnumber}`}>
                              {itemInfo?.phone_isd_code} {itemInfo.phone_pnumber}{" "}
                              {itemInfo?.phone_extn && <> x {itemInfo.phone_extn} </>}
                            </a>
                          </span>
                        )}
                        {itemInfo?.website && (
                          <span className={cx(style["d-flex"], style["align-items-center"], style["me-4"])}>
                            <Icon icon="ui-internet" additionalclass={"me-1 font-base"} />
                            <a
                              className={cx(style["text-decoration-underline"], "website-link", {
                                [style["color-black"]]:
                                  canvasConfigData.sectionBodyContentColor === "#000000" ||
                                  !canvasConfigData.sectionBodyContentColor,
                                [style["color-white"]]: canvasConfigData.sectionBodyContentColor === "#ffffff",
                              })}
                              data-url={`${itemInfo.website}`}>
                              Website
                            </a>
                          </span>
                        )}
                      </p>
                    )}

                  {/* General */}
                  {itemInfo?.description && textCountFn(itemInfo.description) > 0 && (
                    <div className={cx(style["mb-3"])} dangerouslySetInnerHTML={{ __html: itemInfo.description }}></div>
                  )}
                </div>

                {itemInfo?.images?.length > 0 && (
                  <ul
                    className={cx(style["row"], style["image-list"], style["mb-4"], style["pt-2"])}
                    data-image-urls={itemInfo?.images?.map(item => item.url).join(", ")}>
                    {itemInfo?.images?.slice(0, 4)?.map((imageInfo, imageIdx) => (
                      <li
                        className={cx(style["col-auto"], "itinerary-image")}
                        key={imageIdx}
                        data-image-number={`${imageIdx}`}>
                        <div className={cx(style["rounded"])} style={{ maxWidth: "148px" }}>
                          <img src={imageInfo?.url} className={style["img-fluid"]} />
                        </div>

                        {itemInfo?.images?.length > 4 && imageIdx == 3 && (
                          <div className="counter">
                            <div className={style["counter-box"]}>+{`${itemInfo.images.length - 4}`}</div>
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                )}

                {itemInfo?.documents?.length > 0 && (
                  <ul
                    className={cx(
                      style["row"],
                      style["document-list"],
                      style["mb-3"],
                      style["pt-2"],
                      style["color-black"]
                    )}>
                    {itemInfo?.documents?.map((assetInfo, assetIdx) => (
                      <li className={cx(style["col-6"])} key={assetIdx}>
                        <div
                          id={`${assetInfo.id}`}
                          className={cx(style["document-list-item"])}
                          data-asset-url={`${assetInfo.url}`}
                          data-file-name={`${assetInfo?.name}`}>
                          <div className={cx(style["d-flex"], style["align-items-center"], style["w-75"])}>
                            <div className={style["icon-box"]}>
                              <Icon icon="ui-attach" />
                            </div>
                            <div className={style["text-truncate"]}>{assetInfo?.name}</div>
                          </div>
                          <div className={cx(style["d-flex"], style["align-items-center"])}>
                            <a>
                              <Icon icon="ui-download2" additionalclass={"p-1 font-base"} />
                            </a>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
};

const AutoResizingTextarea = ({ itemValue, slug, updateSectionValue, maxLength }) => {
  const textCountFn = htmlDataStr => {
    const textOnly = htmlDataStr.replace(/<[^>]*>/g, "");
    return textOnly.length;
  };

  // For Description (start)
  const [inputFieldValue, setInputFieldValue] = useState("");
  const [isFocused, setIsFocused] = useState(false);
  const [tooltipFor, setTooltipFor] = useState({});
  const [tooltipTarget, setTooltipTarget] = useState();
  const [showTooltip, setShowTooltip] = useState(false);
  const [isBoldeAvailable, setIsBoldeAvailable] = useState(false);
  const [defaltFontBold, setDefaltFontBold] = useState(false);
  const [numberingAcive, setNumberingAcive] = useState(false);
  const [bulletActive, setBulletActive] = useState(false);
  const [bulletDisable, setBulletDisable] = useState(false);
  const [textCount, setTextCount] = useState(itemValue ? textCountFn(itemValue) : 0);

  const inputValueRef = useRef();
  const tooltipBoundry = document.querySelector(".dhp-appContainer");

  // For Description (end)
  // ********* Description starts here..********
  const handleChange = e => {
    // Allow changes if:
    // 1. No maxLength is set, OR
    // 2. The new value is shorter (deletion), OR
    // 3. The new value length is within maxLength
    // if (maxLength && e.target.value.length > maxLength) return;

    if (!e.target) return;
    let value = e.target.value;

    // Trim value to maxLength if it exceeds
    if (maxLength && value.length > maxLength) {
      value = value.substring(0, maxLength);
    }
    inputValueRef.current = value;

    setTextCount(textCountFn(value));
  };

  const resetBulletNumber = type => {
    setNumberingAcive(type == "number" ? true : false);
    setBulletActive(type == "bullet" ? true : false);
    // setBulletDisable(type == "bullet" ? false : false)
  };

  function preserveCaretDuringFormatting(action, reference) {
    const sel = window.getSelection();
    if (!sel.rangeCount) return action();

    const range = sel.getRangeAt(0);
    const startContainer = range.startContainer;
    const startOffset = range.startOffset;

    const activeEditor = document.activeElement.closest('[contenteditable="true"]');
    if (!activeEditor) return;

    // Identify the contenteditable ancestor
    const editor = activeEditor;

    // Run your formatting function (may alter DOM)
    action();

    // Restore focus
    if (editor) editor.focus();

    // Restore caret near old position if possible
    const newSel = window.getSelection();
    const newRange = document.createRange();

    try {
      // Try to restore to the same container
      if (startContainer.isConnected) {
        newRange.setStart(startContainer, Math.min(startOffset, startContainer.length || 0));
        newRange.collapse(true);
      } else if (editor) {
        // Fallback: put caret at the end of editor
        newRange.selectNodeContents(editor);
        newRange.collapse(false);
      }

      newSel.removeAllRanges();
      newSel.addRange(newRange);
    } catch (e) {
      // Silent fallback
    }
  }

  // Flatten nested divs
  const flattenDiv = el => {
    let changed = true;
    while (changed) {
      changed = false;
      const nested = el.querySelectorAll("div > div");
      nested.forEach(inner => {
        const parent = inner.parentNode;
        if (parent && parent.tagName === "DIV" && inner.parentNode.childNodes.length === 1) {
          while (inner.firstChild) parent.insertBefore(inner.firstChild, inner);
          inner.remove();
          changed = true;
        }
      });
    }
  };

  const expandRangeToBoldBoundaries = range => {
    // Expand selection start to include entire bold span
    let startNode = range.startContainer;
    let startBold =
      startNode.nodeType === 3
        ? startNode.parentNode.closest('span[style*="font-weight"]')
        : startNode.closest('span[style*="font-weight"]');

    if (startBold) {
      range.setStartBefore(startBold);
    }

    // Expand selection end to include entire bold span
    let endNode = range.endContainer;
    let endBold =
      endNode.nodeType === 3
        ? endNode.parentNode.closest('span[style*="font-weight"]')
        : endNode.closest('span[style*="font-weight"]');

    if (endBold) {
      range.setEndAfter(endBold);
    }
  };

  const selectedPartFontFormatting = fontFormat => {
    let selectedPart, sel, parentEl, activeHtml, range, html, rangeParentElement;

    let removeSelection = false;

    if (window.getSelection) {
      sel = window.getSelection();
      let selectedCount = sel.toString().trim();
      if (selectedCount.length == 0) {
        return;
      }

      const activeEditor = document.activeElement.closest('[contenteditable="true"]');
      if (!activeEditor) return;

      if (window.getSelection) {
        if (sel.rangeCount) {
          parentEl = sel.getRangeAt(0).commonAncestorContainer;
          if (parentEl.nodeType != 1) {
            parentEl = parentEl.parentNode;
          }
        }
      }

      if (sel.rangeCount) {
        if (parentEl.innerText.trim() === sel.toString().trim() && !parentEl.hasAttribute("contenteditable")) {
          selectedPart = parentEl;
        } else {
          let container = document.createElement("div");
          for (let i = 0, len = sel.rangeCount; i < len; ++i) {
            container.appendChild(sel.getRangeAt(i).cloneContents());
          }

          let listItems = container.querySelectorAll("li");
          listItems.forEach(li => {
            if (!li.textContent.trim()) {
              li.remove();
            }
          });
          activeHtml = container.innerHTML;
          selectedPart = document.createElement("span");
          selectedPart.innerHTML = activeHtml;
        }
      }

      if (sel.getRangeAt && sel.rangeCount) {
        range = window.getSelection().getRangeAt(0);

        rangeParentElement = range.commonAncestorContainer;

        if (fontFormat === "bold") {
          if (!sel.rangeCount) return;
          const range = sel.getRangeAt(0);
          expandRangeToBoldBoundaries(range);

          let cursorTimeout;

          const placeCursorAndReturn = () => {
            // Clear any existing timeout
            if (cursorTimeout) {
              clearTimeout(cursorTimeout);
            }

            cursorTimeout = setTimeout(() => {
              const editor = activeEditor;
              if (!editor) return;

              editor.normalize();

              const sel = window.getSelection();
              if (!sel) {
                editor.focus();
                return;
              }

              if (!sel.rangeCount) {
                const r = document.createRange();
                r.selectNodeContents(editor);
                r.collapse(false);
                sel.addRange(r);
                editor.focus();
                return;
              }

              let anchorNode = sel.anchorNode;
              if (anchorNode && anchorNode.nodeType === Node.TEXT_NODE) {
                anchorNode = anchorNode.parentNode;
              }

              if (!anchorNode || !editor.contains(anchorNode)) {
                const r = document.createRange();
                r.selectNodeContents(editor);
                r.collapse(false);
                sel.removeAllRanges();
                sel.addRange(r);
              }

              editor.focus();
            }, 10);
          };

          const commonAncestor = range.commonAncestorContainer;
          const node = commonAncestor.nodeType === 1 ? commonAncestor : commonAncestor.parentNode;

          const rangeDivs = Array.from(activeEditor.querySelectorAll("div")).filter(div => {
            const r = document.createRange();
            r.selectNodeContents(div);
            return (
              range.compareBoundaryPoints(Range.END_TO_START, r) < 0 &&
              range.compareBoundaryPoints(Range.START_TO_END, r) > 0
            );
          });

          const singleDiv = node.closest("div");
          if (
            singleDiv &&
            !singleDiv.closest("li") &&
            sel.toString().trim().length > 0 &&
            sel.toString().trim() !== singleDiv.innerText.trim() &&
            rangeDivs.length <= 1
          ) {
            const selectedText = sel.toString();

            const rangeHTML = range.cloneContents();
            const container = document.createElement("div");
            container.appendChild(rangeHTML);
            const hasBold = container.querySelector("span[style*='font-weight:700']");

            let replacementHTML = "";
            if (hasBold) {
              replacementHTML = selectedText;
            } else {
              replacementHTML = `<span style="font-weight:700;">${selectedText}</span>`;
            }

            const span = document.createElement("span");
            span.innerHTML = replacementHTML;
            range.deleteContents();
            range.insertNode(span);

            singleDiv.normalize();

            const sel2 = window.getSelection();
            sel2.removeAllRanges();
            const newRange = document.createRange();
            newRange.selectNodeContents(span);
            newRange.collapse(false);
            sel2.addRange(newRange);

            placeCursorAndReturn();
            return;
          }

          const invalidAncestor = node.closest('span[style*="font-weight"] ul, span[style*="font-weight"] ol');
          if (invalidAncestor) {
            const span = invalidAncestor.closest("span");
            if (span) span.replaceWith(...span.childNodes);
            placeCursorAndReturn();
            return;
          }

          const parentUL = node.closest("ul, ol");
          const selectedText = sel.toString().trim();
          if (!selectedText) {
            placeCursorAndReturn();
            return;
          }

          if (parentUL) {
            const allLis = Array.from(parentUL.querySelectorAll("li"));
            const affectedLis = allLis.filter(li => {
              const liRange = document.createRange();
              liRange.selectNodeContents(li);
              return (
                range.compareBoundaryPoints(Range.END_TO_START, liRange) < 0 &&
                range.compareBoundaryPoints(Range.START_TO_END, liRange) > 0
              );
            });

            if (affectedLis.length > 0) {
              const getFirstDescendant = node => {
                let n = node;
                while (n && n.firstChild) n = n.firstChild;
                return n;
              };
              const getLastDescendant = node => {
                let n = node;
                while (n && n.lastChild) n = n.lastChild;
                return n;
              };

              const selRange = range.cloneRange();

              for (let i = 0; i < affectedLis.length; i++) {
                const li = affectedLis[i];
                const innerDiv = li.querySelector("div") || li;
                if (!innerDiv) continue;

                const liRange = document.createRange();
                liRange.selectNodeContents(innerDiv);

                const fullySelected = (() => {
                  try {
                    const liRangeCheck = document.createRange();
                    liRangeCheck.selectNodeContents(innerDiv);

                    if (
                      selRange.compareBoundaryPoints(Range.START_TO_START, liRangeCheck) <= 0 &&
                      selRange.compareBoundaryPoints(Range.END_TO_END, liRangeCheck) >= 0
                    ) {
                      return true;
                    }

                    const selText = selRange.toString().trim();
                    const divText = (innerDiv.textContent || "").trim();
                    if (selText && divText && selText === divText) {
                      return true;
                    }

                    const walker = document.createTreeWalker(innerDiv, NodeFilter.SHOW_TEXT, null, false);
                    let tn;
                    while ((tn = walker.nextNode())) {
                      const tR = document.createRange();
                      tR.selectNodeContents(tn);

                      if (
                        selRange.compareBoundaryPoints(Range.START_TO_START, tR) > 0 ||
                        selRange.compareBoundaryPoints(Range.END_TO_END, tR) < 0
                      ) {
                        return false;
                      }
                    }

                    return true;
                  } catch (e) {
                    return false;
                  }
                })();

                const firstAffected = i === 0;
                const lastAffected = i === affectedLis.length - 1;

                if (fullySelected) {
                  const html = innerDiv.innerHTML.trim();
                  if (!html) continue;
                  const hasBold = /<span[^>]*font-weight:\s*700[^>]*>/.test(html);
                  if (hasBold) {
                    innerDiv.innerHTML = html.replace(/<span[^>]*font-weight:\s*700;?[^>]*>(.*?)<\/span>/gi, "$1");
                  } else {
                    innerDiv.innerHTML = `<span style="font-weight:700;">${html}</span>`;
                  }
                  innerDiv.normalize();
                  continue;
                }

                if (firstAffected || lastAffected) {
                  const subRange = document.createRange();

                  if (firstAffected) {
                    subRange.setStart(selRange.startContainer, selRange.startOffset);

                    const lastNode = getLastDescendant(innerDiv) || innerDiv;
                    if (lastNode.nodeType === Node.TEXT_NODE) {
                      subRange.setEnd(lastNode, lastNode.nodeValue ? lastNode.nodeValue.length : 0);
                    } else {
                      subRange.setEndAfter(lastNode);
                    }
                  }

                  if (lastAffected) {
                    const firstNode = getFirstDescendant(innerDiv) || innerDiv;
                    if (firstNode.nodeType === Node.TEXT_NODE) {
                      subRange.setStart(firstNode, 0);
                    } else if (firstNode) {
                      subRange.setStartBefore(firstNode);
                    } else {
                      subRange.setStartBefore(innerDiv);
                    }
                    subRange.setEnd(selRange.endContainer, selRange.endOffset);
                  }

                  if (firstAffected && lastAffected) {
                    subRange.setStart(selRange.startContainer, selRange.startOffset);
                    subRange.setEnd(selRange.endContainer, selRange.endOffset);
                  }
                  const subClone = subRange.cloneContents();
                  const probe = document.createElement("div");
                  probe.appendChild(subClone);

                  const cloneHasBold = !!probe.querySelector('b, strong, span[style*="font-weight"], .bold, .fw-bold');

                  const nodeHasBoldAncestor = (node, stopAt) => {
                    if (!node) return false;
                    let cur = node.nodeType === 3 ? node.parentNode : node;
                    while (cur && cur !== stopAt && cur !== document) {
                      if (cur.matches && cur.matches("b, strong")) return true;
                      const style = cur.getAttribute ? cur.getAttribute("style") || "" : "";
                      if (/font-weight\s*:\s*(?:7\d{2}|bold)/i.test(style)) return true;
                      if (cur.classList && (cur.classList.contains("bold") || cur.classList.contains("fw-bold")))
                        return true;
                      cur = cur.parentNode;
                    }
                    return false;
                  };

                  const boldIntersectsRange = r => {
                    try {
                      const cac =
                        r.commonAncestorContainer.nodeType === 1
                          ? r.commonAncestorContainer
                          : r.commonAncestorContainer.parentNode;
                      if (!cac) return false;
                      const boldEls = Array.from(
                        cac.querySelectorAll('b, strong, span[style*="font-weight"], .bold, .fw-bold')
                      );
                      for (let i = 0; i < boldEls.length; i++) {
                        const el = boldEls[i];
                        const elRange = document.createRange();
                        elRange.selectNodeContents(el);
                        const endsBefore = r.compareBoundaryPoints(Range.END_TO_START, elRange) >= 0;
                        const startsAfter = r.compareBoundaryPoints(Range.START_TO_END, elRange) <= 0;
                        if (!endsBefore && !startsAfter) return true;
                      }
                    } catch (e) {}
                    return false;
                  };

                  const subRangeHasBold =
                    cloneHasBold ||
                    nodeHasBoldAncestor(subRange.startContainer, subRange.commonAncestorContainer) ||
                    nodeHasBoldAncestor(subRange.endContainer, subRange.commonAncestorContainer) ||
                    boldIntersectsRange(subRange);

                  (function unwrapIntersectingBoldInLiveDOM(rng) {
                    try {
                      const cac =
                        rng.commonAncestorContainer.nodeType === 1
                          ? rng.commonAncestorContainer
                          : rng.commonAncestorContainer.parentNode;
                      if (!cac) return;
                      const boldEls = Array.from(
                        cac.querySelectorAll('b, strong, span[style*="font-weight"], .bold, .fw-bold')
                      );
                      for (const el of boldEls) {
                        const elRange = document.createRange();
                        elRange.selectNodeContents(el);
                        const endsBefore = rng.compareBoundaryPoints(Range.END_TO_START, elRange) >= 0;
                        const startsAfter = rng.compareBoundaryPoints(Range.START_TO_END, elRange) <= 0;
                        if (!endsBefore && !startsAfter) {
                          while (el.firstChild) el.parentNode.insertBefore(el.firstChild, el);
                          el.remove();
                        }
                      }
                    } catch (e) {
                      /* ignore safely */
                    }
                  })(subRange);

                  const frag = subRange.extractContents();

                  const tmp = document.createElement("div");
                  tmp.appendChild(frag.cloneNode(true));
                  const tmpClone = tmp.cloneNode(true);

                  const fragText = (tmp.textContent || "").trim();

                  if (!fragText) {
                    continue;
                  }

                  let replacementNode;
                  if (subRangeHasBold) {
                    tmpClone.querySelectorAll('span[style*="font-weight"]').forEach(node => {
                      const tag = node.tagName ? node.tagName.toLowerCase() : "";
                      let isBold = false;
                      if (node.getAttribute) {
                        const style = node.getAttribute("style") || "";
                        if (/font-weight\s*:\s*(?:7\d{2}|bold)/i.test(style)) isBold = true;
                      }

                      if (isBold) {
                        while (node.firstChild) node.parentNode.insertBefore(node.firstChild, node);
                        node.remove();
                      }
                    });

                    tmp.querySelectorAll("span").forEach(s => {
                      if (!s.textContent.trim()) s.remove();
                    });

                    const fragment = document.createDocumentFragment();
                    while (tmpClone.firstChild) fragment.appendChild(tmpClone.firstChild);
                    replacementNode = fragment;
                  } else {
                    const span = document.createElement("span");
                    span.setAttribute("style", "font-weight:700;");
                    span.appendChild(frag);
                    replacementNode = span;
                  }

                  subRange.insertNode(replacementNode);
                  innerDiv.normalize();

                  if (lastAffected) {
                    const sel2 = window.getSelection();
                    sel2.removeAllRanges();
                    const newRange = document.createRange();
                    try {
                      newRange.selectNodeContents(innerDiv);
                      newRange.collapse(false);
                      sel2.addRange(newRange);
                    } catch (e) {
                      const fallback = document.createRange();
                      fallback.selectNodeContents(parentUL || innerDiv);
                      fallback.collapse(false);
                      sel2.removeAllRanges();
                      sel2.addRange(fallback);
                    }
                  }

                  continue;
                }
              }

              const topUL = parentUL;
              if (topUL) topUL.normalize();

              placeCursorAndReturn();
              return;
            } else if (affectedLis.length > 1) {
              const allAlreadyBold = affectedLis.every(li => {
                const div = li.querySelector("div");
                return div && div.querySelector("span[style*='font-weight:700']");
              });

              affectedLis.forEach(li => {
                const div = li.querySelector("div");

                if (!div) {
                  let html = li.innerHTML.trim();
                  if (!html) return;

                  const hasBoldSpan = /<span[^>]*font-weight:\s*700[^>]*>/i.test(html);

                  if (hasBoldSpan) {
                    li.innerHTML = li.innerHTML.replace(/<span[^>]*font-weight:\s*700;?[^>]*>(.*?)<\/span>/gi, "$1");
                  } else {
                    if (/<br\s*\/?>/i.test(html)) {
                      const parts = html.split(/(<br\s*\/?>)/gi);
                      const built = parts
                        .map(seg => {
                          if (/^<br/i.test(seg)) return seg;
                          const trimmed = seg.trim();
                          return trimmed === "" ? "" : `<span style="font-weight:700;">${trimmed}</span>`;
                        })
                        .join("");
                      li.innerHTML = built;
                    } else {
                      li.innerHTML = `<span style="font-weight:700;">${html}</span>`;
                    }
                  }
                  placeCursorAndReturn();
                  return;
                }

                if (allAlreadyBold) {
                  div.innerHTML = div.innerHTML.replace(/<span[^>]*font-weight:\s*700;?[^>]*>(.*?)<\/span>/gi, "$1");
                } else {
                  if (!div.querySelector("span[style*='font-weight:700']")) {
                    div.innerHTML = `<span style="font-weight:700;">${div.innerHTML}</span>`;
                  }
                }
              });
            }
          } else {
            const parentDiv = node.closest("div");
            if (parentDiv && !parentDiv.closest("li")) {
              const selection = window.getSelection();
              if (!selection.rangeCount) return;
              const selRange = selection.getRangeAt(0).cloneRange();

              if (!selRange.toString().trim()) return;

              const editor = activeEditor;

              const startDiv =
                selRange.startContainer.nodeType === 3
                  ? selRange.startContainer.parentNode.closest("div")
                  : selRange.startContainer.closest("div");
              const endDiv =
                selRange.endContainer.nodeType === 3
                  ? selRange.endContainer.parentNode.closest("div")
                  : selRange.endContainer.closest("div");

              if (!startDiv || !endDiv || !editor.contains(startDiv) || !editor.contains(endDiv)) {
                placeCursorAndReturn();
                return;
              }

              const liveDivs = [];
              let cur = startDiv;
              while (cur) {
                if (cur.tagName === "DIV" && editor.contains(cur)) liveDivs.push(cur);
                if (cur === endDiv) break;
                cur = cur.nextElementSibling;
              }
              if (liveDivs.length === 0) return;

              const cloned = selRange.cloneContents();
              const tmpCheck = document.createElement("div");
              tmpCheck.appendChild(cloned);
              const selectionHasBold = !!tmpCheck.querySelector("span[style*='font-weight:700']");

              const cleanNode = node => {
                node.querySelectorAll("span").forEach(s => {
                  if (!s.textContent.trim()) s.remove();
                });
                node.querySelectorAll("div").forEach(d => {
                  if (!d.textContent.trim() && d.children.length === 0) d.remove();
                });
                node.normalize();
              };

              const first = liveDivs[0];
              const last = liveDivs[liveDivs.length - 1];

              if (liveDivs.length === 1) {
                const singleSub = document.createRange();
                singleSub.setStart(selRange.startContainer, selRange.startOffset);
                singleSub.setEnd(selRange.endContainer, selRange.endOffset);

                const frag = singleSub.cloneContents();
                const tmp = document.createElement("div");
                tmp.appendChild(frag);

                const fragHasBold = !!tmp.querySelector("span[style*='font-weight:700']");

                if (fragHasBold) {
                  const extracted = singleSub.extractContents();
                  const temp = document.createElement("div");
                  temp.appendChild(extracted.cloneNode(true));

                  temp.querySelectorAll("span[style*='font-weight:700']").forEach(s => {
                    while (s.firstChild) s.parentNode.insertBefore(s.firstChild, s);
                    s.remove();
                  });

                  temp.querySelectorAll("span").forEach(s => {
                    if (!s.textContent.trim()) s.remove();
                  });

                  singleSub.insertNode(temp.cloneNode(true));
                  first.normalize();
                  cleanNode(first);
                } else {
                  const extracted = singleSub.extractContents();
                  const wrapper = document.createElement("span");
                  wrapper.setAttribute("style", "font-weight:700;");
                  wrapper.appendChild(extracted);
                  singleSub.insertNode(wrapper);
                  first.normalize();
                  cleanNode(first);
                }

                const sel2 = window.getSelection();
                sel2.removeAllRanges();
                const newRange = document.createRange();
                newRange.selectNodeContents(first);
                newRange.collapse(false);
                sel2.addRange(newRange);
                editor.focus();
                placeCursorAndReturn();
                return;
              }

              if (first) {
                const rStart = document.createRange();
                rStart.setStart(selRange.startContainer, selRange.startOffset);

                let lastNodeOfFirst = first;
                while (lastNodeOfFirst && lastNodeOfFirst.lastChild) lastNodeOfFirst = lastNodeOfFirst.lastChild;
                if (lastNodeOfFirst.nodeType === Node.TEXT_NODE) {
                  rStart.setEnd(lastNodeOfFirst, lastNodeOfFirst.nodeValue ? lastNodeOfFirst.nodeValue.length : 0);
                } else {
                  rStart.setEndAfter(first);
                }

                const frag = rStart.cloneContents();
                const tmp = document.createElement("div");
                tmp.appendChild(frag);

                const selectionHasBold =
                  !!range.commonAncestorContainer.closest("span[style*='font-weight:700']") ||
                  !!range.startContainer.parentNode.closest("span[style*='font-weight:700']") ||
                  !!range.endContainer.parentNode.closest("span[style*='font-weight:700']");

                const fragHasBold = !!tmp.querySelector("span[style*='font-weight:700']");

                if (selectionHasBold || fragHasBold) {
                  const boldSpans = first.querySelectorAll("span[style*='font-weight:700']");
                  boldSpans.forEach(span => {
                    const spanRange = document.createRange();
                    spanRange.selectNodeContents(span);

                    if (
                      range.compareBoundaryPoints(Range.END_TO_START, spanRange) < 0 &&
                      range.compareBoundaryPoints(Range.START_TO_END, spanRange) > 0
                    ) {
                      while (span.firstChild) span.parentNode.insertBefore(span.firstChild, span);
                      span.remove();
                    }
                  });

                  first.normalize();
                  cleanNode(first);
                } else {
                  const extracted = rStart.extractContents();
                  const wrapper = document.createElement("span");
                  wrapper.setAttribute("style", "font-weight:700;");
                  wrapper.appendChild(extracted);
                  rStart.insertNode(wrapper);
                  first.normalize();
                  cleanNode(first);
                }
              }

              if (liveDivs.length > 2) {
                for (let i = 1; i < liveDivs.length - 1; i++) {
                  const d = liveDivs[i];
                  const innerHtml = d.innerHTML.trim();
                  if (!innerHtml) continue;

                  if (selectionHasBold) {
                    d.querySelectorAll("span[style*='font-weight:700']").forEach(s => {
                      while (s.firstChild) s.parentNode.insertBefore(s.firstChild, s);
                      s.remove();
                    });
                  } else {
                    const hasAny = !!d.querySelector("span[style*='font-weight:700']");
                    if (!hasAny) {
                      d.innerHTML = `<span style="font-weight:700;">${innerHtml}</span>`;
                    }
                  }
                  d.normalize();
                  cleanNode(d);
                }
              }

              if (last && last !== first) {
                const rEnd = document.createRange();

                let firstNodeOfLast = last;
                while (firstNodeOfLast && firstNodeOfLast.firstChild) firstNodeOfLast = firstNodeOfLast.firstChild;

                if (firstNodeOfLast && firstNodeOfLast.nodeType === Node.TEXT_NODE) {
                  rEnd.setStart(firstNodeOfLast, 0);
                } else if (firstNodeOfLast) {
                  rEnd.setStartBefore(firstNodeOfLast);
                } else {
                  rEnd.setStartBefore(last);
                }

                rEnd.setEnd(selRange.endContainer, selRange.endOffset);

                const frag2 = rEnd.cloneContents();
                const tmp2 = document.createElement("div");
                tmp2.appendChild(frag2);

                const selectionHasBold =
                  !!range.commonAncestorContainer.closest("span[style*='font-weight:700']") ||
                  !!range.startContainer.parentNode.closest("span[style*='font-weight:700']") ||
                  !!range.endContainer.parentNode.closest("span[style*='font-weight:700']");

                const frag2HasBold = !!tmp2.querySelector("span[style*='font-weight:700']");

                if (selectionHasBold || frag2HasBold) {
                  const boldSpans = last.querySelectorAll("span[style*='font-weight:700']");
                  boldSpans.forEach(span => {
                    while (span.firstChild) span.parentNode.insertBefore(span.firstChild, span);
                    span.remove();
                  });

                  last.normalize();
                  cleanNode(last);
                } else {
                  const extracted2 = rEnd.extractContents();
                  const wrapper2 = document.createElement("span");
                  wrapper2.setAttribute("style", "font-weight:700;");
                  wrapper2.appendChild(extracted2);
                  rEnd.insertNode(wrapper2);

                  last.normalize();
                  cleanNode(last);
                }
              }

              const sel2 = window.getSelection();
              sel2.removeAllRanges();
              const newRange = document.createRange();
              if (last) {
                newRange.selectNodeContents(last);
                newRange.collapse(false);
              } else if (first) {
                newRange.selectNodeContents(first);
                newRange.collapse(false);
              } else {
                newRange.selectNodeContents(editor);
                newRange.collapse(false);
              }
              sel2.addRange(newRange);
              editor.focus();
              placeCursorAndReturn();
              return;
            }
          }
          placeCursorAndReturn();
          return;
        }

        if (fontFormat === "bullet" || fontFormat === "number") {
          const currentClass = fontFormat === "bullet" ? "unorder-list" : "order-list";
          const listStyle = fontFormat === "bullet" ? "square" : "decimal";

          const sel = window.getSelection();
          if (!sel.rangeCount) return;
          const range = sel.getRangeAt(0);

          const normalizeToElement = node => (node.nodeType === Node.TEXT_NODE ? node.parentNode : node);

          let startNode = normalizeToElement(range.startContainer);
          let endNode = normalizeToElement(range.endContainer);

          const startDiv = startNode.closest("div");
          const endDiv = endNode.closest("div");

          const editor = activeEditor;
          if (!editor) return;

          let outerDiv = editor.querySelector(":scope > div");
          if (!outerDiv) {
            outerDiv = document.createElement("div");
            editor.appendChild(outerDiv);
          }

          const ancestorUL =
            range.commonAncestorContainer.nodeType === 1
              ? range.commonAncestorContainer.closest("ul")
              : normalizeToElement(range.commonAncestorContainer).closest("ul");

          if (
            ancestorUL &&
            (ancestorUL.classList.contains("unorder-list") || ancestorUL.classList.contains("order-list"))
          ) {
            const selectedLis = [];
            const selRange = range;
            const allLis = Array.from(ancestorUL.querySelectorAll("li"));

            allLis.forEach(li => {
              const liRange = document.createRange();
              liRange.selectNodeContents(li);
              if (
                selRange.compareBoundaryPoints(Range.END_TO_START, liRange) < 0 &&
                selRange.compareBoundaryPoints(Range.START_TO_END, liRange) > 0
              ) {
                selectedLis.push(li);
              }
            });

            if (selectedLis.length === 0) return;

            const startIndex = allLis.indexOf(selectedLis[0]);
            const endIndex = allLis.indexOf(selectedLis[selectedLis.length - 1]);

            const beforeLis = allLis.slice(0, startIndex);
            const selectedLisSlice = allLis.slice(startIndex, endIndex + 1);
            const afterLis = allLis.slice(endIndex + 1);

            const fragment = document.createDocumentFragment();

            if (beforeLis.length > 0) {
              const beforeUL = ancestorUL.cloneNode(false);
              beforeLis.forEach(li => beforeUL.appendChild(li));
              fragment.appendChild(beforeUL);
            }

            selectedLisSlice.forEach(li => {
              const div = document.createElement("div");
              if (li.querySelector("br")) {
                div.innerHTML = li.innerHTML;
              } else {
                div.innerHTML = li.innerHTML;
              }
              flattenDiv(div);
              fragment.appendChild(div);
            });

            if (afterLis.length > 0) {
              const afterUL = ancestorUL.cloneNode(false);
              afterLis.forEach(li => afterUL.appendChild(li));
              fragment.appendChild(afterUL);
            }

            ancestorUL.replaceWith(fragment);

            setTimeout(() => {
              const range = document.createRange();
              range.selectNodeContents(editor);
              range.collapse(false);

              const sel = window.getSelection();
              sel.removeAllRanges();
              sel.addRange(range);

              editor.focus();
            }, 50);

            resetBulletNumber(fontFormat);

            return;
          }

          const divsToConvert = [];
          let node = startDiv;
          while (node) {
            if (node.nodeType === 1 && node.tagName === "DIV" && node.closest('[contenteditable="true"]') === editor) {
              divsToConvert.push(node);
            }
            if (node === endDiv) break;
            node = node.nextSibling;
          }

          if (divsToConvert.length === 0) return;

          const ul = document.createElement("ul");
          ul.className = currentClass;
          ul.style.listStyleType = listStyle;

          divsToConvert.forEach(d => {
            if (d.querySelector("ul, ol")) {
              return;
            }
            flattenDiv(d);

            const parts = d.innerHTML
              .split(/<br\s*\/?>/i)
              .map(p => p.trim())
              .filter(Boolean);

            if (parts.length > 1) {
              const li = document.createElement("li");
              if (d.querySelector("br")) {
                li.innerHTML = d.innerHTML;
              } else {
                const innerDiv = document.createElement("div");
                innerDiv.innerHTML = d.innerHTML;
                li.appendChild(innerDiv);
              }
              ul.appendChild(li);
            } else {
              const li = document.createElement("li");
              const innerDiv = document.createElement("div");
              innerDiv.innerHTML = d.innerHTML;
              li.appendChild(innerDiv);
              ul.appendChild(li);
            }
          });

          const firstDiv = divsToConvert[0];
          const lastDiv = divsToConvert[divsToConvert.length - 1];

          if (!firstDiv || !lastDiv || firstDiv === editor || lastDiv === editor) {
            editor.appendChild(ul);
          } else {
            const rangeToReplace = document.createRange();
            rangeToReplace.setStartBefore(firstDiv);
            rangeToReplace.setEndAfter(lastDiv);

            rangeToReplace.deleteContents();
            rangeToReplace.insertNode(ul);
          }

          editor.normalize();

          setTimeout(() => {
            const lastLi = editor.querySelector("ul li:last-child");
            const range = document.createRange();

            if (lastLi) {
              range.selectNodeContents(lastLi);
              range.collapse(false);
            } else {
              range.selectNodeContents(editor);
              range.collapse(false);
            }

            const sel = window.getSelection();
            sel.removeAllRanges();
            sel.addRange(range);
            editor.focus();
          }, 50);

          resetBulletNumber(fontFormat);

          return;
        }

        if (range.commonAncestorContainer.closest('[contenteditable="true"]') === range.commonAncestorContainer) {
        } else {
          range.deleteContents();
        }

        let el = document.createElement("div");
        el.innerHTML = html;

        let frag = document.createDocumentFragment(),
          node,
          lastNode;
        while ((node = el.firstChild)) {
          lastNode = frag.appendChild(node);
        }

        if (
          rangeParentElement.tagName === "LI" ||
          (rangeParentElement.tagName === "SPAN" && ["bullet", "number"].includes(fontFormat))
        ) {
          if (rangeParentElement.textContent.trim() === "") {
            rangeParentElement.parentNode.removeChild(rangeParentElement);
          }
        } else if (rangeParentElement.tagName === "UL") {
          const liElements = rangeParentElement.querySelectorAll("li");
          liElements.forEach(li => {
            if (li.textContent.trim() === "") {
              li.parentNode.removeChild(li);
            }
          });
        }
        range.insertNode(frag);

        if (removeSelection) window.getSelection().removeAllRanges();
      }
    }
  };

  const applyBoldFn = () => {
    preserveCaretDuringFormatting(() => selectedPartFontFormatting("bold"));
  };

  const handleBulletApply = () => {
    preserveCaretDuringFormatting(() => selectedPartFontFormatting("bullet"));
  };

  const handleBlur = () => {
    const compareTo = inputValueRef.current;
    if (itemValue === compareTo) return;
    let appliedValue = inputFieldValue;

    appliedValue = inputValueRef.current;
    setInputFieldValue(appliedValue);

    updateSectionValue({ [slug]: appliedValue });

    setIsFocused(false);
  };

  useEffect(() => {
    setInputFieldValue(itemValue);
    // setTextCount(textCountFn(itemValue));
  }, [itemValue]);

  // ********* Description ends here... *********

  return (
    <div className="form-group">
      <label>Description</label>
      <DescriptionAutoResizingTextarea
        inputFieldValue={inputFieldValue}
        handleBlur={handleBlur}
        handleChange={handleChange}
        setTooltipTarget={setTooltipTarget}
        setTooltipFor={setTooltipFor}
        setShowTooltip={setShowTooltip}
        showTooltip={showTooltip}
        setDefaltFontBold={setDefaltFontBold}
        setBulletActive={setBulletActive}
        setBulletDisable={setBulletDisable}
        maxCharacter={maxLength}
        defaultHeight={100}
        minHeight={100}
        autoGrow={true}
      />
      <div className={cx(style["d-flex"], style["justify-content-end"])} style={{ marginTop: ".2rem" }}>
        <div>
          {textCount}/{maxLength}
        </div>
      </div>
      {tooltipTarget && (
        <Tooltip
          key={`tooltip-${tooltipTarget.style.top}-${tooltipTarget.style.left}`}
          placement="top-start"
          target={tooltipTarget}
          isOpen={showTooltip}
          className="table-editable-tooltip"
          boundariesElement={tooltipBoundry}
          hideArrow={true}>
          <FloatingTooltipComponent
            tooltipFor={tooltipFor}
            applyBoldFn={applyBoldFn}
            handleBulletApply={handleBulletApply}
            bulletActive={bulletActive}
            numberingAcive={numberingAcive}
            defaltFontBold={defaltFontBold}
            bulletDisable={bulletDisable}
            isBoldeAvailable={isBoldeAvailable}
          />
        </Tooltip>
      )}
    </div>
  );
};

const ItemFields = ({
  itemValue,
  slug,
  itemIdx,
  setIsErrorMsgShowing,
  updateSectionValue,
  updateTableConfig,
  isEnable = true,
  fieldType = "text",
  maxLength = 60,
  dropdownContainer,
  currData,
  maxMinLimt,
  itineraryDate,
  isEditMode,
  ...props
}) => {
  const inputRef = useRef(null);

  const defaultValueFields = [
    "departure_airport",
    "check_in_date_and_time",
    "meal_date_and_time",
    "pickup_date_and_time",
    "departure_date_and_time",
    "activity_date_and_Time",
  ];

  const [inputFieldValue, setInputFieldValue] = useState("");
  const [showErrorMsg, setShowErrorMsg] = useState("");

  let defaultValue = {};
  if (fieldType == "phone" && props?.isdOptions) {
    let isoCodeValue = props?.phone_iso_code || props.companyCountryCode;
    defaultValue = props.isdOptions.find(elem => elem.iso_code === isoCodeValue);
  }

  const handleChange = e => {
    // Allow changes if:
    // 1. No maxLength is set, OR
    // 2. The new value is shorter (deletion), OR
    // 3. The new value length is within maxLength
    if (!e.target) return;
    let value = e.target.value;

    // Trim value to maxLength if it exceeds
    if (maxLength && value.length > maxLength) {
      value = value.substring(0, maxLength);
    }
    setInputFieldValue(value);
  };

  const handleBlur = slug => {
    if (itemValue === inputFieldValue) return;
    let appliedValue = inputFieldValue;
    if (fieldType === "url") {
      if (inputFieldValue.trim() == "") {
        setShowErrorMsg("");
        setIsErrorMsgShowing(false);
      } else {
        let checkValidURL = validateUrl(appliedValue);
        setIsErrorMsgShowing(!checkValidURL ? true : false);
        setShowErrorMsg(!checkValidURL ? "Please enter a valid URL" : "");
      }
    }

    updateSectionValue({ [slug]: appliedValue });
  };

  const updateCalendar = e => {
    if (e == inputFieldValue) return;
    let insertValue = e;

    if (fieldType == "date_calendar" && insertValue != "") {
      insertValue = dateConverter(insertValue, props.dateFormat, "toUnix");
      updateSectionValue(insertValue);
    } else if (fieldType == "date_time" && !isNaN(e)) updateSectionValue({ [slug]: insertValue });
  };

  const handlePhoneChange = (e, _, fileldType) => {
    if (fileldType === "iso_code") {
      // Update ISO + ISD
      const iso = e.iso_code;
      const selectedCountry = props.isdOptions.find(c => c.iso_code === iso);

      let iso_code = iso;
      let isd_code = `+${selectedCountry?.isd_code || ""}`;

      updateSectionValue({ [`${slug}_iso_code`]: iso_code, [`${slug}_isd_code`]: isd_code });
    } else if (fileldType === "pnumber") {
      const value = e.target.value;

      if (!e.target.validity.patternMismatch && value.length <= 10) {
        updateSectionValue({ [`${slug}_pnumber`]: value });
      }
    } else if (fileldType === "extn") {
      const value = e.target.value;

      updateSectionValue({ [`${slug}_extn`]: value });
    }
  };

  useEffect(() => {
    if (isEnable) {
      if (!isEditMode && itineraryDate && defaultValueFields.includes(slug) && itemValue === "") {
        updateSectionValue({ [slug]: itineraryDate });
        setInputFieldValue(itineraryDate);
      } else setInputFieldValue(itemValue);
    } else setInputFieldValue("");
  }, [itemValue, isEnable]);

  // Tag iso & isd code using company country
  useEffect(() => {
    setTimeout(() => {
      if (fieldType === "phone" && !props[`${slug}_iso_code`] && defaultValue) {
        // Update ISO + ISD

        let iso_code = defaultValue.iso_code;
        let isd_code = `+${defaultValue?.isd_code || ""}`;

        updateSectionValue({ [`${slug}_iso_code`]: iso_code, [`${slug}_isd_code`]: isd_code });
      }
    }, 300);
  }, []);

  return (
    <>
      {fieldType != "textarea" && fieldType != "phone" && (
        <>
          <Input
            type={fieldType}
            {...(fieldType != "date_calendar" && { value: inputFieldValue })}
            {...(fieldType === "date_calendar" && { defaultValue: inputFieldValue })}
            onChange={e => handleChange(e)}
            onBlur={() => handleBlur(slug)}
            {...((fieldType === "date_calendar" || fieldType === "date_time") && { updateState: updateCalendar })}
            disabled={!isEnable}
            isEnable={!isEnable}
            innerRef={inputRef}
            {...(fieldType === "url" && { showerror: showErrorMsg })}
            {...props}
          />
          {fieldType != "date_calendar" && fieldType != "date_time" && slug != "website" && (
            <div className={cx(style["d-flex"], style["justify-content-end"])} style={{ marginTop: "-1.3rem" }}>
              <div>
                {inputFieldValue.length}/{maxLength}
              </div>
            </div>
          )}
        </>
      )}
      {fieldType === "phone" && (
        <>
          <Input
            type={fieldType}
            extn={true}
            phoneDefaultValue={defaultValue}
            isdOptions={props.isdOptions}
            fields={{ pnumber: props?.[`${slug}_pnumber`], extn: props?.[`${slug}_extn`] }}
            updateState={handlePhoneChange}
            {...props}
          />
        </>
      )}
      {fieldType === "textarea" && (
        <AutoResizingTextarea
          itemValue={itemValue}
          slug={slug}
          updateSectionValue={updateSectionValue}
          maxLength={maxLength}
        />
      )}
    </>
  );
};

const TripListItems = ({ itemInfo, handleEditClick, tripItemIdx, deleteTripItem, editedData }) => {
  const dropdownRef = useRef(null);

  const [isOpen, setIsOpen] = useState(false);
  const [showModal, setShowModal] = useState(false);

  // Toggle the dropdown
  const toggle = () => {
    setIsOpen(prevState => !prevState);
  };

  const dropdownContainer = document.querySelector(".itinerary-modal");

  const editItemInfo = () => {
    handleEditClick({ ...itemInfo, tripItemIdx: tripItemIdx });
  };

  const deleteHandler = () => {
    deleteTripItem(tripItemIdx);
  };

  const showDeleteModal = () => {
    if (editedData?.tripItemIdx === tripItemIdx) return;
    setShowModal(true);
  };

  const toggleModal = () => {
    setShowModal(prevState => !prevState);
  };

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        const currentDropdown = document.querySelectorAll(".custom-itinerary-dropdown.show")[0];
        if (currentDropdown) {
          // Check if menu has placed top of the dropdown and container present
          if (dropdownRef?.current && dropdownRef.current?.menuRef?.current && dropdownContainer) {
            const parentDropdown = dropdownRef.current.containerRef.current;
            const menuElement = dropdownRef.current.menuRef.current;

            const parentDropdownStyle = parentDropdown.getBoundingClientRect();
            const menuElementStyle = menuElement.getBoundingClientRect();
            if (menuElementStyle?.top < parentDropdownStyle?.top) {
              let calculatedTop = parentDropdownStyle.top + parentDropdownStyle.height + 2;

              menuElement.style.transform = `translate3d(${menuElementStyle.left}px, ${calculatedTop}px, 0px)`;
              menuElement.style.opacity = 1;
            }
            menuElement.style.opacity = 1;
          }
        }
      }, 15);
    }
  }, [isOpen]);

  return (
    <div className={style["trip-list-item"]}>
      <Icon icon="ui-drag2" additionalclass={"drag-icon"} />
      <ul>
        <li className={cx(style["trip-icon"], style["me-3"])}>
          <Icon icon={`ui-${itemInfo?.icon}`} />
        </li>
        <li className={cx(style["trip-title"], style["me-3"], style["flex-grow-1"])}>
          <div className={style["text-truncate"]}>{itemInfo?.title}</div>
        </li>
        <li className={cx(style["trip-images"], style["me-3"])}>
          <ul className="p-0">
            {itemInfo?.images?.slice(0, 3).map((imageInfo, imgIdx) => (
              <li key={imgIdx}>
                <img src={imageInfo?.url} className={style["img-fluid"]} />
              </li>
            ))}
            {itemInfo?.images?.length > 3 && (
              <li>
                <h6 className="m-0">+{itemInfo.images.length - 3}</h6>
              </li>
            )}
          </ul>
        </li>
        <li className={style["more-action"]}>
          <Dropdown ref={dropdownRef} toggle={toggle} isOpen={isOpen}>
            <DropdownToggle tag="a" className={cx(style["dropdown-toggle"])}>
              <Icon icon="ui-more" id={`more-tooltip-${tripItemIdx}`} />
              <UncontrolledTooltip
                placement="top"
                target={`more-tooltip-${tripItemIdx}`}
                trigger="hover"
                boundariesElement={dropdownContainer}>
                More
              </UncontrolledTooltip>
            </DropdownToggle>
            <DropdownMenu
              right
              className={cx(style["shadow"], style["border-0"], style["rounded-sm"], "custom-itinerary-dropdown")}
              container={dropdownContainer ? dropdownContainer : undefined}
              style={dropdownContainer && { opacity: 0 }}>
              <DropdownItem tag="a" className={style["font-14"]} onClick={editItemInfo}>
                <div className={cx(style["d-flex"], style["align-items-center"])}>
                  <Icon icon="ui-edit" additionalclass={"me-2"} />
                  <span>Edit</span>
                </div>
              </DropdownItem>
              <DropdownItem
                tag="a"
                className={cx(style["font-14"], { [style["disable"]]: editedData?.tripItemIdx === tripItemIdx })}>
                <div className={cx(style["d-flex"], style["align-items-center"])} onClick={showDeleteModal}>
                  <Icon icon="ui-trash" additionalclass={"me-2"} />
                  <span>Delete</span>
                </div>
              </DropdownItem>
            </DropdownMenu>
          </Dropdown>
        </li>
      </ul>
      {showModal && (
        <Modal
          showModal={showModal}
          setShowModal={setShowModal}
          component={DeleteElemModal}
          modalTitle={"Delete"}
          modalBody={"Are you sure to delete the trip item? This action can’tbe reversed."}
          modalButtonLabel={"Delete"}
          deleteElem={deleteHandler}
          closeModal={toggleModal}
          backdrop={false}
          contentClassName={"shadow"}
        />
      )}
    </div>
  );
};

const TripItems = ({ tripItemList = [], setSelectedTripItem }) => {
  const [searchTerm, setSearchTerm] = useState("");

  const filteredItems = useMemo(() => {
    return tripItemList.filter(item => item.name.toLowerCase().includes(searchTerm.toLowerCase()));
  }, [searchTerm, tripItemList]);

  const handleSearchChange = e => {
    setSearchTerm(e.target.value);
  };

  const selectTripItem = tripItem => {
    let tripIndex = tripItemList.findIndex(item => item.slug === tripItem.slug);
    if (setSelectedTripItem) setSelectedTripItem(tripIndex);
  };

  return (
    <>
      <div className={cx(style["add-trip"])} style={{}}>
        <h5 className={cx(style["fw-7"])}>Add Trip Item</h5>
        <div className={cx(style["search-area"], style["mb-4"])}>
          <div className={style["searchicon"]}>
            <Icon icon="ui-search" />
          </div>
          <input
            value={searchTerm}
            onChange={handleSearchChange}
            placeholder="Search Trip Items"
            type="text"
            className={style["form-control"]}></input>
        </div>
        <ul className={cx(style["Itinerary-group"])}>
          {filteredItems.map((tripItem, idx) => (
            <li
              key={idx}
              className={cx(style["trip-item"], style[`${tripItem.class}`])}
              onClick={() => selectTripItem(tripItem)}>
              <a>
                <Icon icon={`ui-${tripItem.icon}`} />
                <span className={style["item-text"]}>{tripItem.name}</span>
              </a>
            </li>
          ))}
        </ul>
      </div>
    </>
  );
};

const TripFormSection = ({
  setSelectedTripItem,
  selectedTrip,
  isdOptions,
  addEditTripData,
  documentId,
  companyCluster,
  dateFormat,
  selectedTripItem,
  isEditMode,
  editedData,
  companyCountryCode,
  itineraryDate,
  setCustomMsg,
  isEditClicked,
  setIsEditClicked,
}) => {
  const [tripFormData, setTripFormData] = useState(
    !isEditMode
      ? {
          icon: selectedTrip?.icon,
          images: [],
          documents: [],
          slug: selectedTrip?.slug,
          tripIndex: selectedTripItem,
        }
      : editedData
  );
  const [disableInsert, setDisableInsert] = useState(true);
  const [travelSupplierLookupList, setTravelSupplierLookupList] = useState([]);
  const [isErrorMsgShowing, setIsErrorMsgShowing] = useState(false);

  const dropdownContainer = document.querySelector(".itinerary-modal");

  const dispatch = useDispatch();

  const supplierType = {
    flight: "Airline",
    accommodation: "Accommodation Provider",
    meal: "Restaurant",
    car_rental: "Car Rental Agency",
    cruise: "Cruise Line",
    activity: "Activity Provider",
    transfer: "Transfer Service Provider",
  };

  const saveTripInfo = () => {
    addEditTripData(tripFormData);
  };

  const updateSectionValue = updates => {
    let updatedTable = { ...tripFormData };
    Object.entries(updates).forEach(([slug, value]) => {
      if (updatedTable[slug] !== value) {
        updatedTable[slug] = value;
      }
    });
    setTripFormData(updatedTable);
  };

  const fetchSupplierListByType = sulierType => {
    dispatch(fetchTravelSupplierByType(sulierType)).then(resp => {
      if (resp?.data?.data) {
        setTravelSupplierLookupList(resp.data.data);
      }
    });
  };

  const onChangeFn = (updateValue, hashkey = null) => {
    const formData = new FormData();

    let uploadedImageData = [];

    updateValue.forEach(fileData => {
      formData.append("files", fileData);
      uploadedImageData.push({
        id: `${fileData.lastModified}${generateUniqueHash()}`,
        name: fileData.name,
      });
    });
    setDisableInsert(true);
    dispatch(uploadItineraryAsset(documentId, companyCluster, formData)).then(resp => {
      resp?.result?.file_urls.forEach((item, itemIdx) => {
        uploadedImageData[itemIdx].url = item[itemIdx];
      });
      setTripFormData(prevData => ({
        ...prevData,
        images: [...prevData.images, ...uploadedImageData],
      }));
      setDisableInsert(false);
    });
  };

  const onFileChangeFn = (updateValue, hashkey = null) => {
    const formData = new FormData();
    let uploadedFileData = [];

    updateValue.forEach(fileData => {
      formData.append("files", fileData);
      uploadedFileData.push({
        id: `${fileData.lastModified}${generateUniqueHash()}`,
        name: fileData.name,
      });
    });
    setDisableInsert(true);
    dispatch(uploadItineraryAsset(documentId, companyCluster, formData)).then(resp => {
      resp?.result?.file_urls.forEach((item, itemIdx) => {
        uploadedFileData[itemIdx].url = item[itemIdx];
      });

      setTripFormData(prevData => ({
        ...prevData,
        documents: [...prevData.documents, ...uploadedFileData],
      }));
      setDisableInsert(false);
    });
  };

  const deleteById = (array, idToDelete) => {
    return array.filter(item => item.id !== idToDelete);
  };

  const onDeleteImageFn = assetId => {
    if (!assetId) return;
    let updatedFormData = { ...tripFormData };
    updatedFormData.images = deleteById(updatedFormData.images, assetId);
    setTripFormData(updatedFormData);
  };

  const onFileDeleteFn = fileId => {
    if (!fileId) return;
    let updatedFormData = { ...tripFormData };
    updatedFormData.documents = deleteById(updatedFormData.documents, fileId);
    setTripFormData(updatedFormData);
  };

  // Enable disable form save button
  useEffect(() => {
    // Get required fields from the fields array
    const requiredFields = selectedTrip?.fields.filter(field => field.required);

    // Check if all required fields have values
    let allRequired = true;

    // Check each required field
    requiredFields.forEach(field => {
      const slug = field.slug;
      const hasValue =
        tripFormData.hasOwnProperty(slug) &&
        tripFormData[slug] !== undefined &&
        tripFormData[slug] !== null &&
        tripFormData[slug] !== "";

      // If any required field is missing, set allRequired to false
      if (!hasValue) {
        allRequired = false;
      }
    });

    setDisableInsert(!allRequired);
  }, [tripFormData]);

  const updateLookupValue = (itemInfo, slug) => {
    // For lookup item selected
    if (itemInfo?._id) {
      let taggedField = {
        [slug]: { name: itemInfo.name, id: itemInfo._id },
        // [slug]: itemInfo.name,
        description: itemInfo?.description || "",
      };

      // Tag images
      if (itemInfo?.images?.length > 0) {
        let imageArray = [];
        itemInfo.images.forEach(imageUrl => {
          imageArray.push({
            id: `${generateUniqueHash()}`,
            name: imageUrl.split("/").pop(),
            url: imageUrl,
          });
        });
        taggedField.images = imageArray;
      } else if (!itemInfo?.images) {
        taggedField.images = [];
      }
      // Tag Phone Number
      if (itemInfo?.phone) {
        taggedField.phone_pnumber = itemInfo.phone;
        if (itemInfo?.phone_extn) taggedField.phone_extn = itemInfo.phone_extn;
        taggedField.phone_iso_code = itemInfo.phone_iso_code;
        let filteredIsdCode = isdOptions.find(elem => elem.iso_code === itemInfo.phone_iso_code);
        if (filteredIsdCode) taggedField.phone_isd_code = `+${filteredIsdCode.isd_code}`;
      }

      // Tag address
      if (itemInfo?.address) taggedField.address = itemInfo.address;

      // Tag website
      if (itemInfo?.website) taggedField.website = itemInfo.website;

      // setTripFormData({ ...tripFormData, [slug]: itemInfo });
      setTripFormData({ ...tripFormData, ...taggedField });
    }
    // For custom input value
    else {
      setTripFormData({ ...tripFormData, [slug]: itemInfo });
    }
  };

  const generateMinMaxdate = (type, fieldSlug, tripFormData) => {
    if (type === "max") {
      let maxdate =
        tripFormData[fieldSlug] && tripFormData[fieldSlug] !== "" ? new Date(tripFormData[fieldSlug] * 1000) : "";
      return maxdate;
    } else {
      let mindate =
        tripFormData[fieldSlug] && tripFormData[fieldSlug] !== ""
          ? new Date(tripFormData[fieldSlug] * 1000)
          : new Date();
      return mindate;
    }
  };

  // Update edit form data when it's edit mode
  useEffect(() => {
    if (isEditClicked && isEditMode && editedData?.title) {
      setTimeout(() => {
        setTripFormData(editedData);
        setIsEditClicked(false);
      }, 300);
    }
  }, [isEditClicked]);

  useEffect(() => {
    let type = supplierType[tripFormData.slug];
    fetchSupplierListByType(type);
  }, []);

  return (
    <div className={cx(style["form-section"])}>
      <h5 className={cx(style["fw-7"])}>
        <span className={style["icon-box"]}>
          <Icon icon={`ui-${selectedTrip.icon}`} />
        </span>
        {isEditMode ? "Update" : "Add"} {selectedTrip?.name}
      </h5>
      <div className={cx(style["customScroll"], style["scroll-Y"])}>
        <form className={cx(style["form-area"])}>
          {/* Form fields */}
          {selectedTrip?.fields?.map((fieldInfo, idX) => (
            <React.Fragment key={idX}>
              {fieldInfo.type === "lookup" && (
                <Input
                  label={fieldInfo.name}
                  returnType="formGroup"
                  type="dropdown"
                  required={fieldInfo.required}
                  classNamePrefix="select"
                  dropDownMenuClass="text-capitalize"
                  getOptionValue={option => option?.name}
                  getOptionLabel={option => <span>{option?.name}</span>}
                  options={travelSupplierLookupList}
                  defaultValue={tripFormData?.[fieldInfo.slug] || {}}
                  updateState={e => updateLookupValue(e, fieldInfo.slug)}
                  showCustomInputOption={true}
                  container={dropdownContainer}
                  closeOnScroll={true}
                />
              )}
              {fieldInfo.type !== "lookup" && (
                <ItemFields
                  label={fieldInfo.name}
                  fieldType={fieldInfo.type}
                  slug={fieldInfo.slug}
                  setIsErrorMsgShowing={setIsErrorMsgShowing}
                  updateSectionValue={updateSectionValue}
                  maxLength={fieldInfo.slug === "website" ? 1000 : fieldInfo?.maxLength}
                  required={fieldInfo.required}
                  dateFormat={dateFormat}
                  // placeholder={fieldInfo?.placeholder || ""}
                  {...(fieldInfo.type !== "phone" && {
                    placeholder: fieldInfo?.placeholder || "",
                  })}
                  isEditMode={isEditMode}
                  {...(fieldInfo.type == "phone" && {
                    isdOptions: isdOptions,
                    [`${fieldInfo.slug}_iso_code`]: tripFormData?.[`${fieldInfo.slug}_iso_code`],
                    [`${fieldInfo.slug}_pnumber`]: tripFormData?.[`${fieldInfo.slug}_pnumber`],
                    [`${fieldInfo.slug}_extn`]: tripFormData?.[`${fieldInfo.slug}_extn`],
                    companyCountryCode: companyCountryCode,
                  })}
                  {...(fieldInfo.type === "date_time" && {
                    itemValue: tripFormData?.[fieldInfo.slug] || "",
                    itineraryDate: itineraryDate,
                    isDisabled: true,
                    maxDate: fieldInfo.maxDate ? generateMinMaxdate("max", fieldInfo.maxDate, tripFormData) : "",
                    minDate: fieldInfo.minDate ? generateMinMaxdate("min", fieldInfo.minDate, tripFormData) : "",
                  })}
                  {...(fieldInfo.type !== "phone" &&
                    fieldInfo.type !== "date_time" && { itemValue: tripFormData?.[fieldInfo.slug] || "" })}
                />
              )}
            </React.Fragment>
          ))}

          {/* Images Section */}
          <div className={cx(style["form-group"], style["mb-4"])}>
            <label className={style["title-border"]}>
              <div className={cx(style["d-flex"], style["align-items-center"])}>
                Images
                <div className={cx(style["custom-tooltip"], style["ps-2"])}>
                  <Icon icon="information" additionalclass={"font-12"} />
                  <span className={cx(style["custom-tooltip-content"], style["bottom"])}>
                    You can add up to 6 images in JPG, <br></br>
                    PNG, WebP format at 5 MB each
                  </span>
                </div>
              </div>
            </label>

            {/* Attachment Section */}
            <div className={style["image-container-list"]}>
              <AssetField
                type={"image"}
                acceptType={"image/jpeg, image/png, image/webp"}
                maxSize={{ image: 5 }}
                defaultAssets={tripFormData?.images || []}
                currentAssetList={tripFormData?.images || []}
                updateFunction={onChangeFn}
                deleteAssetFn={onDeleteImageFn}
                maxCount={6}
                setCustomMsg={setCustomMsg}
                emptyAssetList={true}
              />
            </div>
          </div>
          <div className={cx(style["form-group"], style["document-upload"])}>
            <label className={style["title-border"]}>
              <div className={cx(style["d-flex"], style["align-items-center"])}>
                Documents
                <div className={cx(style["custom-tooltip"], style["ps-2"])}>
                  <Icon icon="information" additionalclass={"font-12"} />
                  <span className={cx(style["custom-tooltip-content"], style["bottom"])}>
                    You can add up to 2 documents in <br></br>
                    PDF format at 5 MB each
                  </span>
                </div>
              </div>
            </label>

            {/* Document Asset upload field */}
            <DocumentAssetField
              maxCount={2}
              defaultAssets={tripFormData?.documents || []}
              currentAssetList={tripFormData?.documents || []}
              updateFunction={onFileChangeFn}
              deleteAssetFn={onFileDeleteFn}
              setCustomMsg={setCustomMsg}
            />
          </div>
        </form>
      </div>

      <div className={style["left-pane-footer"]}>
        <button
          type="button"
          onClick={saveTripInfo}
          className={cx(style["btn"], style["btn-primary"])}
          disabled={disableInsert || isErrorMsgShowing}>
          {!isEditMode ? "Save" : "Update"}
        </button>
        <button type="button" onClick={() => setSelectedTripItem()} className={cx(style["btn"], style["btn-border"])}>
          Cancel
        </button>
      </div>
    </div>
  );
};

const Itinerary = ({ closeModal, modalFullScreen, setModalFullScreen, editTable }) => {
  let { metadata, widgets, updateWidgets } = useContext(EditorContext);
  const { id: documentId } = useParams();
  const dispatch = useDispatch();

  const triggerInUse = useProposalInUseModule();

  const tableDefaultData = JSON.parse(JSON.stringify(widgetConfig?.itinerary));

  const date_format = useSelector(state => state?.auth?.user?.date_format) ?? "MM-DD-YYYY";
  const resourceOptions = useSelector(state => state?.resources);
  const companyInfo = useSelector(state => state?.auth?.company);

  // Update edit table config
  if (editTable) editTable = { ...editTable, tripItems: tableDefaultData?.itineraryConfig?.tripItems };

  const [isEditable, setIsEditable] = useState(false);
  const [insertHtmlMode, setInsertHtmlMode] = useState(false);
  const [insertedHtmlData, setInsertedHtmlData] = useState([]);
  const [disableInsert, setDisableInsert] = useState(true);
  const [addWidgetClicked, setAddWidgetClicked] = useState(false);
  const [dataparam, setDataParam] = useState();
  const [assetInnerContent, setAssetInnerContent] = useState();
  const [widgetWidth, setWidgetWidth] = useState();
  const [widgetHeight, setWidgetHeight] = useState();
  const [customCss, setCustomCss] = useState(false);
  const [tableConfigData, setTableConfigData] = useState(editTable ? editTable : tableDefaultData?.itineraryConfig);
  const [selectedTripItem, setSelectedTripItem] = useState(false);
  const [sectionItems, setSectionItems] = useState(tableConfigData?.items);
  const [readyToUpdate, setReadyToUpdate] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editedData, setEditedData] = useState({});
  const [latestLookupIds, setLatestLookupIds] = useState([]);
  const [customMsg, setCustomMsg] = useState();
  const [isEditClicked, setIsEditClicked] = useState(false);

  const { items: dragItems, containerProps, getItemProps } = useDragAndDrop(sectionItems, "vertical", setReadyToUpdate);

  const htmlMode = false;

  const { postion: getPosition } = useAlignment(addWidgetClicked, widgetWidth, widgetHeight, "middle-center");
  const getNewWidgetObject = useAddWidget(
    addWidgetClicked,
    ITINERARY,
    dataparam,
    getPosition,
    widgetWidth,
    widgetHeight
  );
  useElementInnerHtml(
    addWidgetClicked,
    setAddWidgetClicked,
    ITINERARY,
    assetInnerContent,
    dataparam,
    getNewWidgetObject,
    customCss
  );

  let dateFormat = date_format.replaceAll("-", "/");

  const setFullScreen = () => {
    setModalFullScreen(!modalFullScreen);
  };

  const close = () => {
    closeModal && closeModal();
    if (modalFullScreen) setModalFullScreen(false);
  };

  const handleEditClick = editedInfo => {
    setSelectedTripItem(editedInfo?.tripIndex);
    setIsEditMode(true);
    setEditedData(editedInfo);
    setIsEditClicked(true);
  };

  const updateItineraryInfo = value => {
    let updatedTable = { ...tableConfigData };
    updatedTable.itinerary_date = value;
    setTableConfigData(updatedTable);
  };

  const addEditTripData = selectedTripInfo => {
    if (typeof selectedTripInfo?.tripItemIdx === "number") {
      let sectionItemData = [...sectionItems];
      sectionItemData[selectedTripInfo.tripItemIdx] = selectedTripInfo;
      // Update trip item list info
      setSectionItems(sectionItemData);
    } else {
      setSectionItems([...sectionItems, selectedTripInfo]);
    }
    setSelectedTripItem();
  };

  const handleRenameItineraryTtle = e => {
    let updatedTable = { ...tableConfigData };
    setIsEditable(!isEditable);
    if (e.target.textContent.length === 0) {
      e.target.textContent = updatedTable.itinerary_name; // Revert to previous value
      return;
    }
    updatedTable.itinerary_name = e.target.textContent;
    setTableConfigData(updatedTable);
  };

  const changeSectionName = e => {
    const maxLength = 40; // Set your desired max length
    if (e.currentTarget.textContent.length > maxLength) {
      e.currentTarget.textContent = e.currentTarget.textContent.substring(0, maxLength);
    }
  };

  const onKeyDownFn = e => {
    const maxLength = 40;
    const currentText = e.currentTarget.textContent;

    // Allow Ctrl+A (Select All), deletions, and navigation keys
    const allowedKeys = [
      "Backspace",
      "Delete",
      "ArrowLeft",
      "ArrowRight",
      "ArrowUp",
      "ArrowDown",
      "Tab",
      "Home",
      "End",
    ];

    // Allow Ctrl+A (keyCode 65 with Ctrl pressed)
    const isCtrlA = (e.ctrlKey || e.metaKey) && e.key === "a";

    // Block input if maxLength reached (unless allowed key)
    if (currentText.length >= maxLength && !allowedKeys.includes(e.key) && !isCtrlA) {
      e.preventDefault();
    }

    // Prevent Enter key (optional)
    if (e.key === "Enter") {
      e.preventDefault();
      e.currentTarget.blur(); // Optionally blur on Enter
    }
  };

  const editItineraryName = () => {
    setIsEditable(!isEditable);
    // Focus and place cursor at end after state update
    setTimeout(() => {
      const editableDiv = document.getElementById(`itinerary-day-title`);
      if (editableDiv) {
        editableDiv.focus();
        return;
      }
    }, 0);
  };

  const filterSections = sectionArray => {
    const uniqueIds = [];
    // Loop through each section
    sectionArray.forEach(section => {
      // Switch case to check section.slug
      switch (section.slug) {
        case "flight":
          if (section?.airline?.id && !uniqueIds.includes(section?.airline?.id)) {
            uniqueIds.push(section.airline.id);
          }
          break;

        case "accommodation":
          if (section?.property_name?.id && !uniqueIds.includes(section?.property_name?.id)) {
            uniqueIds.push(section.property_name.id);
          }
          break;

        case "meal":
          if (section?.restaurant_name?.id && !uniqueIds.includes(section?.restaurant_name?.id)) {
            uniqueIds.push(section.restaurant_name.id);
          }
          break;

        case "car_rental":
          if (section?.rental_agency_name?.id && !uniqueIds.includes(section?.rental_agency_name?.id)) {
            uniqueIds.push(section.rental_agency_name.id);
          }
          break;

        case "cruise":
          if (section?.cruise_line?.id && !uniqueIds.includes(section?.cruise_line?.id)) {
            uniqueIds.push(section.cruise_line.id);
          }
          break;

        case "activity":
          if (section?.provider_name?.id && !uniqueIds.includes(section?.provider_name?.id)) {
            uniqueIds.push(section.provider_name.id);
          }
          break;

        case "transfer":
          if (section?.provider_name?.id && !uniqueIds.includes(section?.provider_name?.id)) {
            uniqueIds.push(section.provider_name.id);
          }
          break;

        default:
          break;
      }
    });

    return uniqueIds;
  };

  const insertTable = () => {
    let insertedTableData = { ...tableConfigData, items: dragItems };

    setInsertedHtmlData({ ...insertedTableData });

    // Below code to set in-use proposal modules
    // let inUseProposalModuleList = {
    //   travel_suppliers: filterSections(dragItems),
    // };

    // dispatch(setUsedProposalModule(companyInfo._id, { document_id: documentId }, inUseProposalModuleList));
    if (htmlMode) setInsertHtmlMode(true);
    // Use this htmlString for insert
    if (!editTable) {
      const htmlString = ReactDOMServer.renderToString(<CanvasHtmlData canvasConfigData={insertedTableData} />);
      const elem = document.createElement("div");
      elem.innerHTML = htmlString;

      setLatestLookupIds(filterSections(dragItems));

      // add pheudo elem in editor
      const parent = document.getElementById(metadata.activeBlockId);
      const pheudoElem = document.createElement("div");
      pheudoElem.id = "pheudoElem";
      pheudoElem.style.width = "700px";
      pheudoElem.innerHTML = htmlString;
      parent.appendChild(pheudoElem);

      // insert into editor
      setTimeout(() => {
        const jsonString = JSON.stringify(insertedTableData);
        let paramObj = { "data-table-config": jsonString };
        let dataAttr = { ...widgetConfig[ITINERARY].dataAttr, ...paramObj };
        let offsetHeight = document.querySelector("#pheudoElem .itinerary_editor")?.offsetHeight;
        let offsetWidth = 700;
        let scale = 1242 / offsetWidth; // 1242 is page width(which is static)

        document.querySelector("#pheudoElem").remove();

        setCustomCss({
          transform: `scale(${scale})`,
          width: `${offsetWidth}px`,
          height: `${offsetHeight}px`,
        }); // set width height to inner div
        setWidgetWidth(offsetWidth * scale); // set width  to main div
        setWidgetHeight(offsetHeight * scale); // set height to main div
        setAssetInnerContent(elem);
        setDataParam(dataAttr);
        setAddWidgetClicked(true);
      }, 50);
    }
    // update
    else {
      const jsonString = JSON.stringify(insertedTableData);
      const htmlString = ReactDOMServer.renderToString(<CanvasHtmlData canvasConfigData={insertedTableData} />);
      const elem = document.createElement("div");
      elem.innerHTML = htmlString;
      document.querySelector(`#${metadata.activeWidgetId[0]} .dhp-widget-inner`).innerHTML = elem.innerHTML;

      // add pheudo elem in editor
      const parent = document.getElementById(metadata.activeBlockId);
      const pheudoElem = document.createElement("div");
      pheudoElem.id = "pheudoElem";
      pheudoElem.style.width = document.querySelector(`#${metadata.activeWidgetId[0]} .dhp-widget-inner`).style.width;
      pheudoElem.innerHTML = htmlString;
      parent.appendChild(pheudoElem);

      setTimeout(() => {
        let offsetHeight = document.querySelector("#pheudoElem .itinerary_editor")?.offsetHeight;
        let offsetWidth = parseFloat(
          document.querySelector(`#${metadata.activeWidgetId[0]} .dhp-widget-inner`).style.width
        );
        let scale = parseFloat(
          document.querySelector(`#${metadata.activeWidgetId[0]} .dhp-widget-inner`).style.transform.split("(")[1]
        );
        document.querySelector("#pheudoElem").remove();
        document.querySelector(`#${metadata.activeWidgetId[0]} .dhp-widget-inner`).style.width = `${offsetWidth}px`;
        document.querySelector(`#${metadata.activeWidgetId[0]} .dhp-widget-inner`).style.height = `${offsetHeight}px`;
        document.querySelector(`#${metadata.activeWidgetId[0]} .dhp-widget-inner`).style.transform = `scale(${scale})`;

        const targetWidgetIndex = widgets.findIndex(widget => widget.id === metadata.activeWidgetId[0]);

        let newArray = Object.assign([...widgets]);
        newArray = Object.assign([...newArray], {
          [targetWidgetIndex]: {
            ...newArray[targetWidgetIndex],
            data: {
              ...newArray[targetWidgetIndex].data,
              "data-table-config": jsonString,
            },
            style: {
              ...newArray[targetWidgetIndex].style,
              width: `${offsetWidth * scale}px`,
              height: `${offsetHeight * scale}px`,
            },
            innerHTML: document.getElementById(metadata.activeWidgetId[0]).innerHTML,
          },
        });
        updateWidgets(newArray);
      }, 50);

      setTimeout(() => {
        triggerInUse("ITINERARY");
        close();
      }, 100);
    }
  };

  // Delete Trip item
  const deleteTripItem = tripIndex => {
    let modifiedItems = [...dragItems];
    modifiedItems.splice(tripIndex, 1);
    setSectionItems(modifiedItems);
    if (isEditMode) {
      setIsEditMode(false);
      setEditedData({});
      setSelectedTripItem();
    }
  };

  useEffect(() => {
    if (!readyToUpdate) {
      setReadyToUpdate(false);
      return;
    }
    let updatedTable = { ...tableConfigData };
    updatedTable.items = dragItems;
    setTableConfigData(updatedTable);
    setReadyToUpdate(false);
    if (isEditMode) {
      setIsEditMode(false);
      setEditedData({});
      setSelectedTripItem();
    }
  }, [dragItems]);

  useEffect(() => {
    if (tableConfigData?.items) {
      setSectionItems(tableConfigData.items);
    }
  }, [tableConfigData.items]);

  // Below Code to create a Default record one time onload
  useEffect(() => {
    // Update table config data
    setTableConfigData(tableConfigData);
  }, []);

  // Close pricing table modal after innserted to canvas
  useEffect(() => {
    if (getNewWidgetObject?.id && !htmlMode) {
      triggerInUse("ITINERARY", [], latestLookupIds);
      setLatestLookupIds([]);
      close();
    }
  }, [getNewWidgetObject]);

  // Check if selectedTripItem is not a number
  const isNotNumber = isNaN(selectedTripItem) || typeof selectedTripItem !== "number";

  // Fetch countries resources if not present
  useEffect(() => {
    if (!resourceOptions?.countries) dispatch(getResourceEntries("countries"));
  }, [resourceOptions?.["countries"]]);

  useEffect(() => {
    if (isEditMode && isNotNumber) {
      setIsEditMode(false);
      setEditedData({});
    }
  }, [isEditMode, selectedTripItem]);

  return (
    <>
      {!insertHtmlMode && (
        <div className={style["table-modal-wrap"]}>
          <div className="d-flex justify-content-between modal-header">
            <h4 className={cx(style["fw-7"], style["mb-0"])}>Itinerary</h4>
            <div className={style["header-center"]}>
              {/* <FlashMessage flashMessage={customMsg} /> */}
              <ModalFlashMessage flashMessage={customMsg} />
            </div>
            <ul className={style["d-flex"]}>
              <li className={cx(style["expand-btn"], style["rounded"])} onClick={setFullScreen}>
                <Icon icon={modalFullScreen ? "ui-collapse" : "ui-expand"} />
              </li>
              <li className={cx(style["cross-modal"], style["rounded"])} onClick={close}>
                <Icon icon="ui-close" />
              </li>
            </ul>
          </div>

          <div
            className={cx(style["modal-body"], style["row"], style["p-0"], style["m-0"])}
            // style={{ height: getModalHeight(), maxHeight: "calc(84% - 2rem)" }}
          >
            <div className={cx(style["left-pane"])}>
              {isNotNumber && (
                <TripItems tripItemList={tableConfigData.tripItems} setSelectedTripItem={setSelectedTripItem} />
              )}
              {!isNotNumber && (
                <TripFormSection
                  setSelectedTripItem={setSelectedTripItem}
                  insertTable={insertTable}
                  selectedTripItem={selectedTripItem}
                  selectedTrip={tableConfigData.tripItems[selectedTripItem]}
                  isdOptions={resourceOptions?.["countries"] || []}
                  addEditTripData={addEditTripData}
                  documentId={documentId}
                  dateFormat={dateFormat}
                  companyCluster={companyInfo?.cluster_code}
                  companyCountryCode={companyInfo?.country_code}
                  isEditMode={isEditMode}
                  editedData={editedData}
                  disableInsert={disableInsert}
                  itineraryDate={tableConfigData?.itinerary_date}
                  setCustomMsg={setCustomMsg}
                  isEditClicked={isEditClicked}
                  setIsEditClicked={setIsEditClicked}
                />
              )}
            </div>

            <div
              className={cx(style["right-pane"], style["d-flex"], style["flex-grow-1"], {
                [style["default-itinerary"]]: dragItems?.length < 1,
                [style["record-itinerary"]]: dragItems?.length > 0,
              })}>
              <Card className={cx(style["w-100"])}>
                <CardHeader className={cx(style["d-flex"], style["align-items-center"])}>
                  <div className="custom-date-picker me-4">
                    <ItemFields
                      itemValue={
                        tableConfigData?.itinerary_date !== ""
                          ? dateConverter(tableConfigData?.itinerary_date, dateFormat, "toString")
                          : ""
                      }
                      fieldType={"date_calendar"}
                      dateFormat={dateFormat}
                      id={`tripdate`}
                      updateSectionValue={updateItineraryInfo}
                      required={true}
                      isDisabled={true}
                      minDate={new Date()}
                    />
                  </div>
                  <h6 className={cx(style["mb-0"], style["fw-7"], style["edit-title"], style["d-flex"])}>
                    <div
                      id={`itinerary-day-title`}
                      contentEditable={isEditable}
                      onBlur={e => handleRenameItineraryTtle(e)}
                      onInput={e => changeSectionName(e)}
                      onKeyDown={e => onKeyDownFn(e)}
                      suppressContentEditableWarning={true}
                      className={cx({ [style["rename"]]: isEditable })}>
                      {tableConfigData?.itinerary_name}
                    </div>
                    {!isEditable && (
                      <a onClick={() => editItineraryName()}>
                        <Icon icon="ui-edit" />
                      </a>
                    )}
                  </h6>
                </CardHeader>
                <CardBody className={style["p-0"]}>
                  {/* Defauls empty list message */}
                  {dragItems?.length < 1 && (
                    <div className={cx(style["defalt"])}>
                      <div className={cx(style["defalt-info"])}>
                        <span className={style["icon-box"]}>
                          <Icon icon="ui-idea" />
                        </span>
                        <p className="fw-7">How it works</p>
                        <p>
                          Each itinerary table, like this one, is for trip items for a single day. <br></br>
                          Planning a multi-day journey? Simply add a new itinerary table <br></br>
                          for each additional day.
                        </p>
                        <p>
                          Ready? Choose a <span className="fw-7">Trip Item </span> from the left pane to get started!
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Record list */}
                  {dragItems?.length > 0 && (
                    <div className={style["trip-details"]}>
                      <div className={style["trip-lable"]}>
                        <ul className={cx(style["d-flex"], style["align-items-center"])}>
                          <li></li>
                          <li className={cx(style["trip-lable-title"], style["flex-grow-1"], style["me-3"])}>Title</li>
                          <li className={style["trip-lable-image"]}>Images</li>
                          <li></li>
                        </ul>
                      </div>

                      <div
                        className={cx(style["trip-list"], style["customScroll"], style["scroll-Y"])}
                        {...containerProps}>
                        {dragItems?.map((itemInfo, itemIdx) => (
                          <div key={itemIdx} {...getItemProps(itemIdx)}>
                            <TripListItems
                              tripItemIdx={itemIdx}
                              itemInfo={itemInfo}
                              handleEditClick={handleEditClick}
                              deleteTripItem={deleteTripItem}
                              editedData={editedData}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </CardBody>
              </Card>
              {dragItems?.length > 0 && (
                <div className={cx(style["right-pane-footer"])}>
                  <button
                    type="button"
                    onClick={insertTable}
                    className={cx(style["btn"], style["btn-primary"])}
                    disabled={selectedTripItem || isEditMode || tableConfigData?.itinerary_date == ""}>
                    {!editTable ? "Insert" : "Update"}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* below section need to delete after insert html page design done */}
      {insertHtmlMode && (
        <Modal
          size="lg"
          showModal={insertHtmlMode}
          component={CanvasHtmlData}
          canvasConfigData={insertedHtmlData}
          setShowModal={setInsertHtmlMode}
        />
      )}
    </>
  );
};

export default Itinerary;
